import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Clock, User, Globe } from 'lucide-react';
import { format } from 'date-fns';
import { Booking, getBooking, deleteBooking, updateBooking, syncMobileBookingsForBooking, getFieldQuotesForBooking, linkFieldQuotesToBooking } from '../lib/bookings';
import ScheduleFieldQuoteModal from './ScheduleFieldQuoteModal';
import { getProposal, Proposal } from '../lib/proposals';
import { cleanupBookingProducts, createStockTransaction, StockItem } from '../lib/stockItems';
import { createBookingCompletedMilestone } from '../lib/bookingMilestones';
import type { BookingResource, BookingService } from '../types/bookings';
import { deleteDiscount } from '../lib/discounts';
import { Material } from '../lib/materials';
import { useBusiness } from '../contexts/BusinessContext';
import { usePermissions } from '../contexts/PermissionContext';
import { staffResourceId, upsellLineStamp } from '../lib/upsellLines';
import { useAppSystem } from '../hooks/useAppSystem';
import { supabase } from '../lib/supabase'; 
import BookingHeader from './booking/BookingHeader';
import BookingStatusBar from './booking/BookingStatusBar';
import BookingTabs from './booking/BookingTabs';
import MobileTabStatusHeader from './booking/MobileTabStatusHeader';
import BookingContent from './booking/BookingContent';
import BookingPaymentSummary from './booking/BookingPaymentSummary';
import RecordPaymentModal from './RecordPaymentModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import CustomerDetailsModal from './CustomerDetailsModal';
import SelectCustomerModal from './manage/SelectCustomerModal';
import SelectMaterialModal from './manage/SelectMaterialModal';
import EditVehicleModal from './booking/EditVehicleModal';
import SelectVehicleModal from './manage/SelectVehicleModal';
import CreateMobileBookingModal from './CreateMobileBookingModal';
import SelectServiceModal from './manage/SelectServiceModal';
import SelectResourceModal from './manage/SelectResourceModal';
import ApplyDiscountModal from './ApplyDiscountModal';
import EditTitleModal from './booking/EditTitleModal';
import BookingColorPickerModal from './BookingColorPickerModal';
import CreateVehicleModal from './CreateVehicleModal';
import type { Customer, CustomerVehicle } from '../lib/customers';
import { getCustomer, getCustomerVehicle } from '../lib/customers';
import type { Payment } from '../types/payments';
import { paymentPrincipal, isPaymentCountedForBalance } from '../lib/paymentBreakdown';
import { useBookingInvoice } from '../hooks/useBookingInvoice';
import { processCanceledBooking } from '../services/canceledBookingProcessor';
import { processBookingStatusChange } from '../services/bookingStatusProcessor';
import { checkBookingStatusTriggers, checkBookingStatus } from '../services/debugHelper';
import { deleteFeedbackForBooking } from '../lib/feedback';
import { triggerBookingAutomations } from '../lib/automationsV2';
import { cancelBookingAutomations } from '../services/automationCancellationService';
// Removed all persistence systems
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import { getIncompleteWorkflowSteps, completeAllWorkflowSteps } from '../lib/bookingWorkflows';
import { getBookingDocumentRequirements } from '../lib/documents';
import BookingDocumentsGateModal from './documents/BookingDocumentsGateModal';
import { getIncompleteFlatRateGroups, completeAllFlatRateGroups } from '../lib/flat-rates';
import WorkflowCompletionModal from './booking/WorkflowCompletionModal';
import FlatRateCompletionModal from './booking/FlatRateCompletionModal';
import WorkflowResourceAssignmentModal from './WorkflowResourceAssignmentModal';
import { getWorkflowByServiceId, getWorkflowSteps } from '../lib/workflows';
import type { ServiceWorkflow, WorkflowStep } from '../lib/workflows';
import CreateBookingModal from './CreateBookingModal';
import BetaPaymentsModal from './BetaPaymentsModal';
import CreateProposalModal from './CreateProposalModal';
import ProposalDetailsModal from './ProposalDetailsModal';
import { ModalStackProvider, useModalZIndex } from '../contexts/ModalStackContext';

interface BookingInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: Booking | null;
  onUpdate: () => void;
  readOnly?: boolean;
  onRescheduleMobileBooking?: () => void;
  /**
   * Override the modal's stacking z-index. Defaults to 99999. Only needed when
   * opening on top of another high-z modal (e.g. from CustomerDetailsModal).
   * The inner layers live in this container's isolated stacking context, so
   * bumping this base value lifts the whole modal.
   */
  zIndex?: number;
}

// Debug flag to control logging - set to false to disable verbose logs
const enableInfoModalDebug = false;

export default function BookingInfoModal({ 
  isOpen, 
  onClose, 
  booking, 
  onUpdate,
  readOnly = false,
  onRescheduleMobileBooking,
  zIndex = 99999
}: BookingInfoModalProps) {
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const { currentResource } = usePermissions();
  const { canAccess, canViewAdminDashboard } = usePermissionCheck();
  const [isBookingDocsGateOpen, setIsBookingDocsGateOpen] = useState(false);
  
  // App system hooks
  const { coreApps, installedAddons } = useAppSystem();
  
  // Check if apps are installed
  const isInventoryAppInstalled = coreApps.some(app => app.app?.slug === 'inventory') || 
                                  installedAddons.some(app => app.app?.slug === 'inventory');
  const isPayrollAppInstalled = coreApps.some(app => app.app?.slug === 'payroll') || 
                               installedAddons.some(app => app.app?.slug === 'payroll');
  const isExpensesAppInstalled = coreApps.some(app => app.app?.slug === 'expenses') || 
                                installedAddons.some(app => app.app?.slug === 'expenses');
  const isProposalsAppInstalled = coreApps.some(app => app.app?.slug === 'proposals') || 
                                  installedAddons.some(app => app.app?.slug === 'proposals');
  
  // State for app settings - start with false to prevent flash
  const [showMaterialsInBookings, setShowMaterialsInBookings] = useState(false);
  const [showFlatRateInBookings, setShowFlatRateInBookings] = useState(false);
  const [inventorySettingsLoaded, setInventorySettingsLoaded] = useState(false);
  const [payrollSettingsLoaded, setPayrollSettingsLoaded] = useState(false);
  
  // Load inventory settings
  useEffect(() => {
    const loadInventorySettings = async () => {
      if (!selectedBusiness?.id) {
        setInventorySettingsLoaded(true);
        return;
      }

      if (!isInventoryAppInstalled) {
        setShowMaterialsInBookings(false);
        setInventorySettingsLoaded(true);
        return;
      }

      try {
        const { data, error } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'inventory',
            p_setting_key: 'show_materials_in_bookings',
            p_default_value: true
          });

        if (!error && data !== null) {
          setShowMaterialsInBookings(data);
        } else {
          // Default to true if no setting exists
          setShowMaterialsInBookings(true);
        }
      } catch (error) {
        console.error('Error loading inventory settings:', error);
        // Default to true on error
        setShowMaterialsInBookings(true);
      } finally {
        setInventorySettingsLoaded(true);
      }
    };

    loadInventorySettings();

    // Listen for setting changes
    const handleInventorySettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'show_materials_in_bookings') {
        setShowMaterialsInBookings(event.detail.value);
      }
    };

    window.addEventListener('inventorySettingChanged', handleInventorySettingChange as EventListener);

    return () => {
      window.removeEventListener('inventorySettingChanged', handleInventorySettingChange as EventListener);
    };
  }, [selectedBusiness?.id, isInventoryAppInstalled]);

  // Load payroll settings
  useEffect(() => {
    const loadPayrollSettings = async () => {
      console.log('🔍 loadPayrollSettings called:', {
        selectedBusinessId: selectedBusiness?.id,
        isPayrollAppInstalled
      });

      if (!selectedBusiness?.id) {
        console.log('🔍 No business ID, setting payroll loaded to true');
        setPayrollSettingsLoaded(true);
        return;
      }

      if (!isPayrollAppInstalled) {
        console.log('🔍 Payroll app NOT installed, setting showFlatRateInBookings to FALSE');
        setShowFlatRateInBookings(false);
        setPayrollSettingsLoaded(true);
        return;
      }

      console.log('🔍 Payroll app IS installed, loading settings...');
      try {
        const { data, error } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'payroll',
            p_setting_key: 'show_flat_rate_in_bookings',
            p_default_value: true
          });

        console.log('🔍 Payroll settings RPC result:', { data, error });

        if (!error && data !== null) {
          console.log('🔍 Setting showFlatRateInBookings to:', data);
          setShowFlatRateInBookings(data);
        } else {
          // Default to true if no setting exists (but only if app is installed)
          console.log('🔍 No setting found, defaulting to true (app is installed)');
          setShowFlatRateInBookings(true);
        }
      } catch (error) {
        console.error('Error loading payroll settings:', error);
        // Default to true on error (but only if app is installed)
        console.log('🔍 Error occurred, defaulting to true (app is installed)');
        setShowFlatRateInBookings(true);
      } finally {
        setPayrollSettingsLoaded(true);
      }
    };

    loadPayrollSettings();

    // Listen for setting changes
    const handlePayrollSettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'show_flat_rate_in_bookings') {
        setShowFlatRateInBookings(event.detail.value);
      }
    };

    window.addEventListener('payrollSettingChanged', handlePayrollSettingChange as EventListener);

    return () => {
      window.removeEventListener('payrollSettingChanged', handlePayrollSettingChange as EventListener);
    };
  }, [selectedBusiness?.id, isPayrollAppInstalled]);

  // Load the "Create Field Quotes from bookings" calendar setting. Only enabled
  // when both Field Quotes and the nested toggle are on.
  useEffect(() => {
    let cancelled = false;

    const loadSetting = async () => {
      if (!selectedBusiness?.id) {
        setCreateFqFromBookings(false);
        return;
      }
      try {
        const [{ data: fqEnabled }, { data: fromBookings }] = await Promise.all([
          supabase.rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_field_quotes_in_fab',
            p_default_value: false,
          }),
          supabase.rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'create_field_quotes_from_bookings',
            p_default_value: false,
          }),
        ]);
        if (!cancelled) setCreateFqFromBookings(!!fqEnabled && !!fromBookings);
      } catch (error) {
        console.error('Error loading create_field_quotes_from_bookings setting:', error);
        if (!cancelled) setCreateFqFromBookings(false);
      }
    };

    loadSetting();

    const handleCalendarSettingChange = (event: CustomEvent) => {
      if (event.detail?.key === 'create_field_quotes_from_bookings' || event.detail?.key === 'show_field_quotes_in_fab') {
        loadSetting();
      }
    };

    window.addEventListener('calendarSettingChanged', handleCalendarSettingChange as EventListener);

    return () => {
      cancelled = true;
      window.removeEventListener('calendarSettingChanged', handleCalendarSettingChange as EventListener);
    };
  }, [selectedBusiness?.id]);
  
  const [activeTab, setActiveTab] = useState('overview');
  // Whether this booking has any linked field quote — drives the "Field Quote" tab.
  const [hasFieldQuote, setHasFieldQuote] = useState(false);
  // Calendar setting: allow scheduling a field quote directly from a booking.
  const [createFqFromBookings, setCreateFqFromBookings] = useState(false);
  const [isScheduleFqOpen, setIsScheduleFqOpen] = useState(false);
  const [scheduleFqCustomer, setScheduleFqCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(false);
  const [permissionLevel, setPermissionLevel] = useState<'manage' | 'view' | 'disabled'>('manage');
  const [error, setError] = useState<string | null>(null);
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [isCustomerDetailsModalOpen, setIsCustomerDetailsModalOpen] = useState(false);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [isSelectResourceModalOpen, setIsSelectResourceModalOpen] = useState(false);
  const [assetsEnabled, setAssetsEnabled] = useState(false);
  const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
  // VIN just captured by the booking-line photo scan; decoded live inside the vehicle modal.
  const [scannedVinForEdit, setScannedVinForEdit] = useState<string | null>(null);
  const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const [selectedFlatRate, setSelectedFlatRate] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [currentVehicleId, setCurrentVehicleId] = useState<string | null>(null);
  const [localBooking, setLocalBooking] = useState<Booking | null>(null);

  // Live invoice for this booking — used so Take Payment charges the invoice
  // balance (not the legacy booking total) when an invoice exists.
  const { invoice: liveInvoice, refetch: refetchLiveInvoice } = useBookingInvoice(localBooking?.id);

  // Re-check whether a field quote is linked (controls the "Field Quote" tab).
  const refreshHasFieldQuote = useCallback(async () => {
    const bookingId = booking?.id;
    if (!isOpen || !bookingId) {
      setHasFieldQuote(false);
      return;
    }
    try {
      const rows = await getFieldQuotesForBooking(bookingId);
      setHasFieldQuote(rows.length > 0);
    } catch {
      setHasFieldQuote(false);
    }
  }, [isOpen, booking?.id]);

  useEffect(() => {
    void refreshHasFieldQuote();
  }, [refreshHasFieldQuote]);

  // Open the field-quote scheduler pre-seeded with this booking's customer.
  const handleOpenScheduleFieldQuote = useCallback(async () => {
    const customerId = localBooking?.customer?.id;
    if (customerId) {
      try {
        const full = await getCustomer(customerId);
        setScheduleFqCustomer(full ?? (localBooking?.customer as unknown as Customer) ?? null);
      } catch {
        setScheduleFqCustomer((localBooking?.customer as unknown as Customer) ?? null);
      }
    } else {
      setScheduleFqCustomer(null);
    }
    setIsScheduleFqOpen(true);
  }, [localBooking?.customer]);
  const [linkedProject, setLinkedProject] = useState<{ id: string; title: string } | null>(null);
  const [linkedOpportunity, setLinkedOpportunity] = useState<{ id: number; title: string; value: number; status: string } | null>(null);
  const [scheduleChanged, setScheduleChanged] = useState(false);
  const [currentVehicle, setCurrentVehicle] = useState<any>(null);
  const [isSelectMaterialModalOpen, setIsSelectMaterialModalOpen] = useState(false);
  const [isApplyDiscountModalOpen, setIsApplyDiscountModalOpen] = useState(false);
  const [isSelectFlatRateModalOpen, setIsSelectFlatRateModalOpen] = useState(false);
  const [isEditTitleModalOpen, setIsEditTitleModalOpen] = useState(false);
  const [isBookingColorPickerModalOpen, setIsBookingColorPickerModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editColor, setEditColor] = useState('');
  const [editSecondaryColor, setEditSecondaryColor] = useState<string | undefined>(undefined);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isWorkflowCompletionModalOpen, setIsWorkflowCompletionModalOpen] = useState(false);
  const [incompleteWorkflowSteps, setIncompleteWorkflowSteps] = useState(0);
  const [isFlatRateCompletionModalOpen, setIsFlatRateCompletionModalOpen] = useState(false);
  const [incompleteFlatRateGroups, setIncompleteFlatRateGroups] = useState(0);
  const [pendingStatusChange, setPendingStatusChange] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [creatorResource, setCreatorResource] = useState<any>(null);
  const [loadingCreator, setLoadingCreator] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Workflow assignment modal state
  const [isWorkflowAssignmentModalOpen, setIsWorkflowAssignmentModalOpen] = useState(false);
  const [currentWorkflow, setCurrentWorkflow] = useState<ServiceWorkflow | null>(null);
  const [currentWorkflowSteps, setCurrentWorkflowSteps] = useState<WorkflowStep[]>([]);
  const [currentServiceIdForWorkflow, setCurrentServiceIdForWorkflow] = useState<string | null>(null);
  
  // Beta payments modal state for header view invoice
  const [isBetaPaymentsModalOpen, setIsBetaPaymentsModalOpen] = useState(false);
  const [isRecordPaymentModalOpen, setIsRecordPaymentModalOpen] = useState(false);
  const [isCreateProposalFromBookingOpen, setIsCreateProposalFromBookingOpen] = useState(false);
  const [bookingProposals, setBookingProposals] = useState<any[]>([]);
  const [selectedProposalForView, setSelectedProposalForView] = useState<Proposal | null>(null);
  const [isProposalDetailsOpen, setIsProposalDetailsOpen] = useState(false);

  // Conflict check state for resource/asset assignment
  const [conflictWarning, setConflictWarning] = useState<{
    type: 'resource' | 'asset';
    item: any;
    conflicts: { booking_number: number; start_time: string; end_time: string; customer: string }[];
  } | null>(null);

  // Manage modal visibility with small delay to prevent flash
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure content is ready before showing light background
      const timer = setTimeout(() => {
        setIsModalVisible(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setIsModalVisible(false);
    }
  }, [isOpen]);
  
  // Damage and waiver state
  const [damageWaiverState, setDamageWaiverState] = useState<{
    isLiabilityWaiverEnabled: boolean;
    liabilityWaiverAgreed: boolean;
    hasDamageReport: boolean;
    liabilityWaiverPdfUrl?: string;
    onSendLiabilityWaiver: () => void;
    onAddDamageReport: () => void;
    isSendingLiabilityWaiver: boolean;
  }>({
    isLiabilityWaiverEnabled: false,
    liabilityWaiverAgreed: false,
    hasDamageReport: false,
    onSendLiabilityWaiver: () => {},
    onAddDamageReport: () => {},
    isSendingLiabilityWaiver: false
  });

  

  
      // Removed all persistence systems
  // No need for duplicate modal persistence systems
  
  // Clean up modal classes when it closes
  useEffect(() => {
    if (!isOpen && !isModalVisible) {
      // Just remove the z-index classes - no body style manipulation needed
      document.body.classList.remove('modal-open-ios');
      document.body.classList.remove('modal-open');
    }
  }, [isOpen, isModalVisible]);



  // Handle window resize events for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch creator resource information
  useEffect(() => {
    const fetchCreatorInfo = async () => {
      if (!localBooking?.created_by || localBooking.created_by === 'admin' || localBooking.created_by === 'online') {
        setCreatorResource(null);
        return;
      }

      setLoadingCreator(true);
      try {
        // Fetch resource information from the database
        if (!supabase) {
          throw new Error('Supabase client not initialized');
        }
        
        const { data: resource, error } = await supabase
          .from('resources')
          .select('id, first_name, last_name, avatar_url')
          .eq('id', localBooking.created_by)
          .single();

        if (error) {
          console.error('Error fetching creator resource:', error);
          setCreatorResource(null);
        } else {
          setCreatorResource(resource);
        }
      } catch (error) {
        console.error('Error fetching creator resource:', error);
        setCreatorResource(null);
      } finally {
        setLoadingCreator(false);
      }
    };

    fetchCreatorInfo();
  }, [localBooking?.created_by]);

  const checkForConflicts = async (
    targetId: string,
    type: 'resource' | 'asset'
  ): Promise<{ booking_number: number; start_time: string; end_time: string; customer: string }[]> => {
    if (!localBooking?.start_date || !localBooking?.start_time || !localBooking?.business_id) return [];

    const bookingStart = `${localBooking.start_date} ${localBooking.start_time}`;
    const bookingEnd = localBooking.end_date && localBooking.end_time
      ? `${localBooking.end_date} ${localBooking.end_time}`
      : bookingStart;

    // Find overlapping bookings for this resource or asset
    // A booking overlaps if: its start < our end AND its end > our start
    const { data: overlapping, error } = await supabase
      .from('bookings')
      .select(`
        id, booking_number, start_date, start_time, end_date, end_time,
        customer:customers!bookings_customer_id_fkey(first_name, last_name)
      `)
      .eq('business_id', localBooking.business_id)
      .eq('is_deleted', false)
      .neq('id', localBooking.id)
      .neq('status', 'cancelled')
      .lte('start_date', localBooking.end_date || localBooking.start_date)
      .gte('end_date', localBooking.start_date);

    if (error || !overlapping) return [];

    // Now check which of these bookings actually have this resource/asset assigned
    const overlappingIds = overlapping.map(b => b.id);
    if (overlappingIds.length === 0) return [];

    let matchingBookingIds: string[] = [];

    if (type === 'resource') {
      const { data: br } = await supabase
        .from('booking_resources')
        .select('booking_id')
        .eq('resource_id', targetId)
        .in('booking_id', overlappingIds);
      matchingBookingIds = (br || []).map(r => r.booking_id);
    } else {
      const { data: ba } = await supabase
        .from('booking_assets')
        .select('booking_id')
        .eq('asset_id', targetId)
        .in('booking_id', overlappingIds);
      matchingBookingIds = (ba || []).map(a => a.booking_id);
    }

    if (matchingBookingIds.length === 0) return [];

    // Filter to only bookings with actual time overlap (not just date overlap)
    return overlapping
      .filter(b => matchingBookingIds.includes(b.id))
      .filter(b => {
        const bStart = `${b.start_date} ${b.start_time || '00:00'}`;
        const bEnd = `${b.end_date || b.start_date} ${b.end_time || '23:59'}`;
        return bStart < bookingEnd && bEnd > bookingStart;
      })
      .map(b => ({
        booking_number: b.booking_number,
        start_time: b.start_time || '',
        end_time: b.end_time || '',
        customer: b.customer ? `${b.customer.first_name} ${b.customer.last_name}` : 'Unknown'
      }));
  };

  const handleAddResource = async (resource: any) => {
    try {
      setLoading(true);
      setError(null);

      if (!resource || !resource.id) {
        throw new Error('Invalid resource selected');
      }

      if (resource.id !== 'unassigned') {
        const conflicts = await checkForConflicts(resource.id, 'resource');
        if (conflicts.length > 0) {
          setConflictWarning({ type: 'resource', item: resource, conflicts });
          setLoading(false);
          return;
        }
      }

      await confirmAddResource(resource);
    } catch (err) {
      console.error('Error adding resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to add resource');
    } finally {
      setLoading(false);
    }
  };

  const confirmAddResource = async (resource: any) => {
    try {
      setLoading(true);
      setError(null);

      const currentResources = localBooking?.resources || [];
      const updatedResources = [
        ...currentResources.map(r => ({
          resource_id: r.resource_id,
          role: r.role,
          service_id: r.service_id,
          start_date: r.start_date,
          end_date: r.end_date
        })),
        {
          resource_id: resource.id === 'unassigned' ? null : resource.id,
          role: 'technician',
          service_id: null,
          start_date: null,
          end_date: null
        }
      ];
      
      if (localBooking?.id) {
        await updateBooking(localBooking.id, { resources: updatedResources });
        setSelectedResources(prev => [...prev, resource.id]);
        await refreshBooking();
        setIsSelectResourceModalOpen(false);
        setConflictWarning(null);

        if (localBooking.booking_lat && localBooking.booking_lng && localBooking.business_id) {
          supabase.functions.invoke('check-booking-proximity', {
            body: { booking_id: localBooking.id, business_id: localBooking.business_id }
          }).catch(err => {
            console.error('Error calling check-booking-proximity:', err);
          });
        }
      } else {
        throw new Error('No booking ID found');
      }
    } catch (err) {
      console.error('Error adding resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to add resource');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveResource = async (resourceId: string) => {
    try {
      setLoading(true);
      if (!localBooking?.id) {
        throw new Error('No booking ID found');
      }

      // Filter out the resource to remove and map to the correct structure
      const updatedResources = localBooking.resources
        ?.filter(r => r.resource_id !== resourceId)
        .map(r => ({
          resource_id: r.resource_id,
          role: r.role,
          service_id: r.service_id,
          start_date: r.start_date,
          end_date: r.end_date
        })) || [];

      await updateBooking(localBooking.id, { resources: updatedResources });
      // Update selectedResources state by removing the resource
      setSelectedResources(prev => prev.filter(id => id !== resourceId));
      await refreshBooking();
    } catch (err) {
      console.error('Error removing resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove resource');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAsset = async (asset: any) => {
    try {
      setLoading(true);
      setError(null);
      if (!localBooking?.id || !asset?.id) throw new Error('Invalid asset or booking');

      const conflicts = await checkForConflicts(asset.id, 'asset');
      if (conflicts.length > 0) {
        setConflictWarning({ type: 'asset', item: asset, conflicts });
        setLoading(false);
        return;
      }

      await confirmAddAsset(asset);
    } catch (err) {
      console.error('Error adding asset:', err);
      setError(err instanceof Error ? err.message : 'Failed to add asset');
    } finally {
      setLoading(false);
    }
  };

  const confirmAddAsset = async (asset: any) => {
    try {
      setLoading(true);
      setError(null);
      if (!localBooking?.id || !asset?.id) throw new Error('Invalid asset or booking');

      const { error: insertError } = await supabase
        .from('booking_assets')
        .insert({
          booking_id: localBooking.id,
          asset_id: asset.id,
          start_date: localBooking.start_date && localBooking.start_time
            ? `${localBooking.start_date} ${localBooking.start_time}` : null,
          end_date: localBooking.end_date && localBooking.end_time
            ? `${localBooking.end_date} ${localBooking.end_time}` : null,
        });

      if (insertError) throw insertError;
      await syncMobileBookingsForBooking(localBooking.id);
      await refreshBooking();
      setIsSelectResourceModalOpen(false);
      setConflictWarning(null);
    } catch (err) {
      console.error('Error adding asset:', err);
      setError(err instanceof Error ? err.message : 'Failed to add asset');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAsset = async (assetId: string) => {
    try {
      setLoading(true);
      if (!localBooking?.id) throw new Error('No booking ID found');

      const { error: deleteError } = await supabase
        .from('booking_assets')
        .delete()
        .eq('booking_id', localBooking.id)
        .eq('asset_id', assetId);

      if (deleteError) throw deleteError;
      // Sync buffer blocks after asset removal
      await syncMobileBookingsForBooking(localBooking.id);
      await refreshBooking();
    } catch (err) {
      console.error('Error removing asset:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove asset');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && booking?.id) {
      // DON'T block body scroll - modal covers entire viewport anyway
      // Just add classes for z-index stacking
      document.body.classList.add('modal-open-ios');
      document.body.classList.add('modal-open');
      setError(null);
      
      // Set localBooking immediately to prevent flash
      setLocalBooking(booking);
      
      // Then load fresh data asynchronously
      loadBookingData(booking.id);

      // Check if assets are enabled for this business
      if (booking.business_id) {
        supabase.rpc('get_app_setting', {
          p_business_id: booking.business_id,
          p_app_slug: 'teams',
          p_setting_key: 'assets_enabled',
          p_default_value: false
        }).then(({ data, error: settingError }) => {
          if (!settingError && data !== null) setAssetsEnabled(data);
          else setAssetsEnabled(false);
        }).catch(() => setAssetsEnabled(false));
      }

      // Restore active tab from saved state if available
      // Removed all persistence systems
      // if (savedState && savedState.activeTab && savedState.bookingId === booking.id) {
      //   console.log('📱 Restoring active tab:', savedState.activeTab);
      //   setActiveTab(savedState.activeTab);
      // }

      // Save modal state to localStorage when it opens
      // console.log('📱 Modal opened, saving state to localStorage');
      // Removed all persistence systems
      //   isOpen: true,
      //   bookingId: booking.id,
      //   bookingData: booking,
      //   activeTab: savedState?.activeTab || activeTab
      // });
    } else if (!isOpen) {
      // Clear modal state when it closes
      // console.log('📱 Modal closed, clearing localStorage state');
      // Removed all persistence systems
    }
  }, [isOpen, booking?.id]);

  // Save active tab changes to localStorage
  useEffect(() => {
    if (isOpen && booking?.id) {
      // Removed all persistence systems
    }
  }, [activeTab, isOpen, booking?.id]);

  // Fetch linked project when booking changes
  useEffect(() => {
    const fetchLinkedProject = async () => {
      if (!localBooking?.id) {
        setLinkedProject(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('projects')
          .select('id, title')
          .eq('booking_id', localBooking.id)
          .single();

        if (error) {
          // No linked project found (PGRST116 = no rows returned)
          if (error.code !== 'PGRST116') {
            console.error('Error fetching linked project:', error);
          }
          setLinkedProject(null);
        } else if (data) {
          setLinkedProject({ id: data.id, title: data.title });
        }
      } catch (err) {
        console.error('Error fetching linked project:', err);
        setLinkedProject(null);
      }
    };

    fetchLinkedProject();
  }, [localBooking?.id]);

  // Fetch linked opportunity when booking changes
  useEffect(() => {
    const fetchLinkedOpportunity = async () => {
      if (!localBooking?.pipeline_opportunities_id) {
        setLinkedOpportunity(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('pipeline_opportunities')
          .select('id, title, value, status')
          .eq('id', localBooking.pipeline_opportunities_id)
          .single();

        if (error) {
          // No linked opportunity found (PGRST116 = no rows returned)
          if (error.code !== 'PGRST116') {
            console.error('Error fetching linked opportunity:', error);
          }
          setLinkedOpportunity(null);
        } else if (data) {
          setLinkedOpportunity({ 
            id: data.id, 
            title: data.title,
            value: data.value || 0,
            status: data.status || 'open'
          });
        }
      } catch (err) {
        console.error('Error fetching linked opportunity:', err);
        setLinkedOpportunity(null);
      }
    };

    fetchLinkedOpportunity();
  }, [localBooking?.pipeline_opportunities_id]);

  // Save booking data changes to localStorage
  useEffect(() => {
    if (isOpen && localBooking) {
      // Removed all persistence systems
    }
  }, [localBooking, isOpen]);



  const loadBookingData = async (bookingId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading booking data:', {
        bookingId,
        hasCustomer: !!booking?.customer,
        customerId: booking?.customer?.id
      });

      const freshBooking = await getBooking(bookingId);
      
      if (!freshBooking) {
        throw new Error(`Booking ${bookingId} not found`);
      }
      
      if (!freshBooking.customer) {
        throw new Error(`No customer found for booking ${bookingId}`);
      }

      if (!freshBooking.customer.id) {
        throw new Error(`Invalid customer ID for booking ${bookingId}`);
      }

      console.log('Booking data loaded:', {
        bookingId: freshBooking.id,
        customerId: freshBooking.customer.id,
        customerName: `${freshBooking.customer.first_name} ${freshBooking.customer.last_name}`,
        services: freshBooking.services?.length
      });

      setLocalBooking(freshBooking);
      // Update selectedResources with current booking resources
      if (freshBooking.resources) {
        const resourceIds = freshBooking.resources.map(r => r.resource_id).filter((id): id is string => id !== null);
        setSelectedResources(resourceIds);
      } else {
        setSelectedResources([]);
      }
    } catch (err) {
      console.error('Error loading booking:', {
        error: err,
        bookingId,
        customerId: booking?.customer?.id
      });
      const errorMessage = err instanceof Error ? err.message : 'Failed to load booking details';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadBookingProposals = async (bookingId: string) => {
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select(`
          *,
          customer:customers!proposals_customer_id_fkey(first_name, last_name),
          offers:proposal_offers(
            *,
            services:proposal_services_with_details(*),
            products:proposal_products_with_details(*)
          )
        `)
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: false });
      if (!error && data) {
        setBookingProposals(data);
      }
    } catch (err) {
      console.error('Error loading booking proposals:', err);
    }
  };

  useEffect(() => {
    if (isOpen && localBooking?.id) {
      loadBookingProposals(localBooking.id);
    }
  }, [isOpen, localBooking?.id]);

  useEffect(() => {
    const handleServicesUpdated = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.bookingId && detail.bookingId === localBooking?.id) {
        loadBookingData(localBooking.id);
        loadBookingProposals(localBooking.id);
        onUpdate();
      }
    };
    window.addEventListener('bookingServicesUpdated', handleServicesUpdated);
    return () => window.removeEventListener('bookingServicesUpdated', handleServicesUpdated);
  }, [localBooking?.id]);

  const refreshBooking = async () => {
    if (!localBooking?.id) {
      console.error('Cannot refresh booking: No booking ID', {
        booking: localBooking
      });
      return;
    }
    
    await loadBookingData(localBooking.id);
    // Re-pull the invoice rollup too (paid/balance/total) — realtime delivery
    // of the invoice row update can lag or be missed, leaving amounts stale.
    void refetchLiveInvoice();
    // Call parent's onUpdate to refresh the entire booking list
    onUpdate();
  };

  // Optimized address update function to prevent loading flash
  const handleAddressUpdate = async (address: string, lat?: number, lng?: number) => {
    if (!localBooking?.id) return;

    try {
      // Update local state immediately to prevent UI flash
      setLocalBooking(prev => {
        if (!prev) return null;
        return {
          ...prev,
          booking_address: address,
          ...(lat != null && { booking_lat: lat }),
          ...(lng != null && { booking_lng: lng })
        } as Booking;
      });

      // Update database - include coordinates so the map pin moves too
      const updateData: Record<string, any> = { booking_address: address };
      if (lat != null) updateData.booking_lat = lat;
      if (lng != null) updateData.booking_lng = lng;
      await updateBooking(localBooking.id, updateData);
      
      // Call parent's onUpdate to refresh the calendar/list view
      onUpdate();
    } catch (err) {
      console.error('Error updating booking address:', err);
      setError(err instanceof Error ? err.message : 'Failed to update booking address');
      
      // Revert local state on error
      if (localBooking) {
        setLocalBooking(prev => {
          if (!prev) return null;
          return {
            ...prev,
            booking_address: localBooking.booking_address
          } as Booking;
        });
      }
    }
  };

  const handleSwitchToBooking = async (bookingId: string) => {
    try {
      setLoading(true);
      setActiveTab('overview'); // Reset to overview tab
      console.log('🎫 Switching to booking:', bookingId);
      
      // Fetch the new booking
      const newBooking = await getBooking(bookingId);
      setLocalBooking(newBooking);
      
      console.log('🎫 ✅ Switched to booking #' + newBooking.booking_number);
    } catch (err) {
      console.error('🎫 ❌ Error switching to booking:', err);
      setError('Failed to load booking');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string, opts?: { skipDocGate?: boolean }) => {
    if (!localBooking?.id) return;

    try {
      setStatusLoading(true);
      setError(null);

      // Store the previous status before updating
      const previousStatus = localBooking.status;
      
      console.log(`[STATUS] Updating booking ${localBooking.id} from ${previousStatus} to ${newStatus}`);

      // Rule 2: Check for incomplete workflow steps when trying to complete booking
      if (newStatus === 'completed') {
        console.log('[WORKFLOW CHECK] Checking for incomplete workflow steps...');
        const incompleteSteps = await getIncompleteWorkflowSteps(localBooking.id);
        console.log(`[WORKFLOW CHECK] Found ${incompleteSteps} incomplete workflow steps`);
        if (incompleteSteps > 0) {
          console.log('[WORKFLOW CHECK] Opening workflow completion modal');
          setIncompleteWorkflowSteps(incompleteSteps);
          setPendingStatusChange(newStatus);
          setIsWorkflowCompletionModalOpen(true);
          setStatusLoading(false);
          return;
        }

        // Rule 3: Check for incomplete flat rate groups when trying to complete booking
        console.log('[FLAT RATE CHECK] Checking for incomplete flat rate groups...');
        const incompleteGroups = await getIncompleteFlatRateGroups(localBooking.id);
        console.log(`[FLAT RATE CHECK] Found ${incompleteGroups} incomplete flat rate groups`);
        if (incompleteGroups > 0) {
          console.log('[FLAT RATE CHECK] Opening flat rate completion modal');
          setIncompleteFlatRateGroups(incompleteGroups);
          setPendingStatusChange(newStatus);
          setIsFlatRateCompletionModalOpen(true);
          setStatusLoading(false);
          return;
        }

        // Rule 4: Required booking documents must be completed (unless an admin overrode).
        if (!opts?.skipDocGate && selectedBusiness?.id) {
          try {
            const bookingDocs = await getBookingDocumentRequirements(selectedBusiness.id, localBooking.id);
            const incompleteRequired = bookingDocs.filter((d) => d.enforcement === 'required' && !d.completed);
            if (incompleteRequired.length > 0) {
              setPendingStatusChange(newStatus);
              setIsBookingDocsGateOpen(true);
              setStatusLoading(false);
              return;
            }
          } catch (docErr) {
            // Fail-open: never block completion because the requirements check itself failed.
            console.error('[BOOKING DOCS] requirement check failed:', docErr);
          }
        }
      }

      // Handle feedback token removal when changing from completed to another status
      if (previousStatus === 'completed' && newStatus !== 'completed') {
        try {
          console.log(`[STATUS] 🗑️ Removing feedback token for booking changing from completed to ${newStatus}`);
          const feedbackDeleted = await deleteFeedbackForBooking(localBooking.id);
          if (feedbackDeleted) {
            console.log(`[STATUS] ✅ Feedback token removed successfully`);
          } else {
            console.warn(`[STATUS] ⚠️ Failed to remove feedback token, continuing with status change`);
          }
        } catch (error) {
          console.error(`[STATUS] ❌ Error removing feedback token:`, error);
          // Don't fail the status change if feedback removal fails
        }
      }

      // Update completed_at timestamp for completed bookings
      let completed_at = localBooking.completed_at;
      if (newStatus === 'completed' && !completed_at) {
        completed_at = new Date().toISOString();
      } else if (newStatus !== 'completed') {
        completed_at = undefined; // Clear completed_at for non-completed statuses
      }

      const result = await updateBooking(localBooking.id, {
        status: newStatus,
        completed_at
      });

      if (!result) {
        throw new Error('Failed to update booking status');
      }

      // Update local state immediately to reflect the change in UI
      setLocalBooking(prev => {
        if (!prev) return null;
        return {
          ...prev,
          status: newStatus,
          completed_at: completed_at || prev.completed_at,
          // Clear feedback_token if changing from completed to another status
          feedback_token: (previousStatus === 'completed' && newStatus !== 'completed') ? null : prev.feedback_token
        } as Booking;
      });

      // Create booking completed milestone when status changes to completed
      if (newStatus === 'completed' && previousStatus !== 'completed') {
        try {
          await createBookingCompletedMilestone(
            localBooking.business_id,
            localBooking.customer?.id || '',
            localBooking.id,
            localBooking.title,
            completed_at
          );
          console.log('✅ Booking completed milestone created successfully');
        } catch (milestoneError) {
          console.error('❌ Error creating booking completed milestone:', milestoneError);
          // Continue even if milestone creation fails
        }
      }

      // If the status is one that triggers automations, process it
      if (newStatus === 'in_progress' || newStatus === 'completed') {
        try {
          // Process the status change to trigger OLD automation system
          console.log(`[STATUS] 🚀 Triggering OLD automation processing for status: ${previousStatus} → ${newStatus}`);
          console.log(`[STATUS] 🚀 Booking ID: ${localBooking.id}`);
          console.log(`[STATUS] 🚀 This should trigger bonus calculation if status is 'completed'`);
          
          const processingResult = await processBookingStatusChange(localBooking.id, newStatus, previousStatus);
          
          console.log(`[STATUS] 🚀 Processing result:`, processingResult);
          
          if (processingResult.success) {
            console.log(`[STATUS] ✅ OLD automation processing completed successfully`);
          } else {
            console.warn(`[STATUS] ⚠️ OLD automation processing had issues: ${processingResult.error}`);
          }
        } catch (error) {
          console.error(`[STATUS] ❌ Error in OLD automation processing:`, error);
          console.error(`[STATUS] ❌ Error stack:`, error instanceof Error ? error.stack : 'No stack');
        }

        // 🎯 Also trigger NEW QStash-based automations for in_progress and completed status changes
        if (selectedBusiness?.id && localBooking) {
          try {
            console.log(`[STATUS] 🚀 Triggering NEW QStash automations for status: ${previousStatus} → ${newStatus}`);
            
                         // Prepare booking data for automation trigger
             const realBookingData = {
               booking_id: localBooking.id,
               booking_number: localBooking.booking_number,
               title: localBooking.title,
               customer: {
                 full_name: `${localBooking.customer?.first_name || ''} ${localBooking.customer?.last_name || ''}`.trim(),
                 phone: localBooking.customer?.phone || undefined,
                 email: localBooking.customer?.email || undefined
               },
               schedule: {
                 drop_off_date: localBooking.drop_off_date || '',
                 drop_off_time: localBooking.drop_off_time || '',
                 start_date: localBooking.start_date || '',
                 start_time: localBooking.start_time || '',
                 end_date: localBooking.end_date || '',
                 end_time: localBooking.end_time || ''
               },
               services: (localBooking.services || []).map(service => ({
                 name: service.name,
                 price: service.price,
                 duration_minutes: service.duration_minutes || 60,
                 vehicle: {
                   make: service.vehicle_make || '',
                   model: service.vehicle_model || '',
                   year: service.vehicle_year || 0,
                   color: service.vehicle_color || ''
                 }
               })),
               vehicles: (() => {
                 const vehicles = (localBooking.services || [])
                   .filter(service => service.vehicle_make || service.vehicle_model)
                   .map(service => ({
                     make: service.vehicle_make || '',
                     model: service.vehicle_model || '', 
                     year: service.vehicle_year || 0,
                     color: service.vehicle_color || '',
                     display: `${service.vehicle_year || ''} ${service.vehicle_make || ''} ${service.vehicle_model || ''}`.trim()
                   }));
                 
                 // Remove duplicates based on make/model/year combination
                 const uniqueVehicles = vehicles.filter((vehicle, index, self) => 
                   index === self.findIndex(v => v.make === vehicle.make && v.model === vehicle.model && v.year === vehicle.year)
                 );
                 
                 return uniqueVehicles;
               })(),
               grand_total: (localBooking as any).grand_total || localBooking.subtotal || 0,
               status: newStatus,
               resources: (localBooking.resources || []).map(bookingResource => ({
                 name: `${bookingResource.resource?.first_name || ''} ${bookingResource.resource?.last_name || ''}`.trim(),
                 email: bookingResource.resource?.email || '',
                 phone: bookingResource.resource?.phone || ''
               })),
               feedback_token: newStatus === 'completed' ? localBooking.feedback_token : undefined,
               source: 'status_change'
             };

            // Map status to event type for automation system
            const eventType = newStatus === 'in_progress' ? 'in_progress' : 'completed';
            
            console.log(`🎯 STATUS CHANGE: Triggering QStash automations with data:`, realBookingData);
            await triggerBookingAutomations(selectedBusiness.id, eventType, realBookingData);
            console.log(`🎯 STATUS CHANGE: QStash automations triggered successfully for ${newStatus}`);
          } catch (automationError) {
            console.error(`❌ STATUS CHANGE: Failed to trigger QStash automations:`, automationError);
            // Don't fail the entire status change process if automation fails
          }

          // Completing a job is a positive moment — good time to ask for an
          // App Store rating (heavily rate-limited inside).
          if (newStatus === 'completed') {
            import('../lib/appReview')
              .then(({ maybeRequestAppReview }) => void maybeRequestAppReview('booking_completed'))
              .catch(() => { /* non-critical */ });
          }
        }
      } else {
        // For other status changes (like completed → scheduled), we still need to handle feedback cleanup
        try {
          console.log(`[STATUS] 🔄 Processing status change for non-automation triggers: ${previousStatus} → ${newStatus}`);
          const processingResult = await processBookingStatusChange(localBooking.id, newStatus, previousStatus);
          
          if (processingResult.success) {
            console.log(`[STATUS] ✅ Status change processing completed successfully`);
          } else {
            console.warn(`[STATUS] ⚠️ Status change processing had issues: ${processingResult.error}`);
          }
        } catch (error) {
          console.error(`[STATUS] ❌ Error in status change processing:`, error);
        }
      }

      onUpdate();
    } catch (err) {
      console.error('[STATUS] ❌ Error updating status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update booking status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    try {
      setStatusLoading(true);
      setError(null);

      if (!localBooking?.id) {
        throw new Error('No booking ID found');
      }

      // If the booking is already cancelled, schedule it
      const newStatus = localBooking.status === 'cancelled' ? 'scheduled' : 'cancelled';
      
      console.log(`[CANCEL] Setting booking ${localBooking.id} to ${newStatus}`);

      // 🚫 Cancel existing automations BEFORE changing booking status
      if (newStatus === 'cancelled' && selectedBusiness?.id) {
        try {
          console.log(`[CANCEL] 🚫 Cancelling existing automations for booking ${localBooking.id}`);
          const cancellationResult = await cancelBookingAutomations(
            localBooking.id,
            'booking_cancelled',
            selectedBusiness.id
          );
          
          if (cancellationResult.success) {
            console.log(`[CANCEL] ✅ Successfully cancelled ${cancellationResult.cancelledJobs} automation jobs`);
          } else {
            console.warn(`[CANCEL] ⚠️ Automation cancellation had issues:`, cancellationResult.errors);
          }
        } catch (error) {
          console.error('[CANCEL] ❌ Error cancelling automations:', error);
          // Don't fail the booking cancellation if automation cancellation fails
        }
      }

      const result = await updateBooking(localBooking.id, { 
        status: newStatus,
        // Clear completed_at when scheduling a cancelled booking
        completed_at: newStatus === 'scheduled' ? null : localBooking.completed_at
      });

      if (!result) {
        throw new Error(`Failed to ${newStatus === 'cancelled' ? 'cancel' : 'schedule'} booking`);
      }

      // If the booking was just canceled (not scheduled), process the cancellation
      if (newStatus === 'cancelled') {
        try {
          // CLEANUP: Remove all products associated with this booking
          console.log(`[CANCEL] Cleaning up products for cancelled booking: ${localBooking.id}`);
          await cleanupBookingProducts(localBooking.id);
          console.log('[CANCEL] Product cleanup completed successfully');
        } catch (cleanupError) {
          console.error('[CANCEL] Error cleaning up booking products:', cleanupError);
          // Don't throw here - we still want to cancel the booking
        }

        try {
          // Process the cancellation to trigger OLD automation system
          console.log(`[CANCEL] Triggering OLD cancellation automation processing`);
          const processingResult = await processCanceledBooking(localBooking.id);
          
          if (processingResult.success) {
            console.log('[CANCEL] OLD automation processing completed successfully');
          } else {
            console.warn('[CANCEL] OLD automation processing had issues:', processingResult.error);
            // Don't throw error here - we still want to show the booking as canceled
          }
        } catch (error) {
          console.error('[CANCEL] Error in OLD automation processing:', error);
          // Don't throw error here - we still want to show the booking as canceled even if notifications fail
        }

        // 🎯 Also trigger NEW QStash-based automations for canceled bookings
        if (selectedBusiness?.id && localBooking) {
          try {
            console.log(`[CANCEL] 🚀 Triggering NEW QStash automations for canceled booking`);
            
            // Prepare booking data for automation trigger
            const realBookingData = {
              booking_id: localBooking.id,
              booking_number: localBooking.booking_number,
              title: localBooking.title,
              customer: {
                full_name: `${localBooking.customer?.first_name || ''} ${localBooking.customer?.last_name || ''}`.trim(),
                phone: localBooking.customer?.phone || undefined,
                email: localBooking.customer?.email || undefined
              },
              schedule: {
                drop_off_date: localBooking.drop_off_date || '',
                drop_off_time: localBooking.drop_off_time || '',
                start_date: localBooking.start_date || '',
                start_time: localBooking.start_time || '',
                end_date: localBooking.end_date || '',
                end_time: localBooking.end_time || ''
              },
              services: (localBooking.services || []).map(service => ({
                name: service.name,
                price: service.price,
                duration_minutes: service.duration_minutes || 60,
                vehicle: {
                  make: service.vehicle_make || '',
                  model: service.vehicle_model || '',
                  year: service.vehicle_year || 0,
                  color: service.vehicle_color || ''
                }
              })),
              vehicles: (() => {
                const vehicles = (localBooking.services || [])
                  .filter(service => service.vehicle_make || service.vehicle_model)
                  .map(service => ({
                    make: service.vehicle_make || '',
                    model: service.vehicle_model || '', 
                    year: service.vehicle_year || 0,
                    color: service.vehicle_color || '',
                    display: `${service.vehicle_year || ''} ${service.vehicle_make || ''} ${service.vehicle_model || ''}`.trim()
                  }));
                
                // Remove duplicates based on make/model/year combination
                const uniqueVehicles = vehicles.filter((vehicle, index, self) => 
                  index === self.findIndex(v => v.make === vehicle.make && v.model === vehicle.model && v.year === vehicle.year)
                );
                
                return uniqueVehicles;
              })(),
              grand_total: (localBooking as any).grand_total || localBooking.subtotal || 0,
              status: 'cancelled',
              resources: (localBooking.resources || []).map(bookingResource => ({
                name: `${bookingResource.resource?.first_name || ''} ${bookingResource.resource?.last_name || ''}`.trim(),
                email: bookingResource.resource?.email || '',
                phone: bookingResource.resource?.phone || ''
              })),
              source: 'cancel_action'
            };

            console.log(`🎯 CANCEL: Triggering QStash automations with data:`, realBookingData);
            await triggerBookingAutomations(selectedBusiness.id, 'canceled', realBookingData);
            console.log(`🎯 CANCEL: QStash automations triggered successfully for canceled booking`);
          } catch (automationError) {
            console.error(`❌ CANCEL: Failed to trigger QStash automations:`, automationError);
            // Don't fail the entire cancellation process if automation fails
          }
        }
      }

      // Update local state
      setLocalBooking(prev => {
        if (!prev) return null;
        return {
          ...prev,
          status: newStatus,
          completed_at: newStatus === 'scheduled' ? null : prev.completed_at
        } as Booking;
      });

      onUpdate();
    } catch (err) {
      console.error('[CANCEL] Error updating booking status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update booking status');
      throw err;
    } finally {
      setStatusLoading(false);
    }
  };

  // Move a booking to the "unscheduled" (limbo) state: clear its date/time fields
  // and flag it so it appears in the unscheduled tab.
  const handleToggleAutomationsDisabled = async (next: boolean) => {
    if (!localBooking?.id) return;
    // Optimistic update
    setLocalBooking(prev => (prev ? ({ ...prev, automations_disabled: next } as Booking) : prev));
    try {
      const { error: toggleError } = await supabase
        .from('bookings')
        .update({ automations_disabled: next })
        .eq('id', localBooking.id);
      if (toggleError) throw toggleError;
    } catch (e) {
      console.error('Failed to update automations_disabled:', e);
      // Revert on failure
      setLocalBooking(prev => (prev ? ({ ...prev, automations_disabled: !next } as Booking) : prev));
      setError('Failed to update automation setting');
    }
  };

  // Keep the "Remove from automations" checkbox in sync with the DB regardless of
  // how the parent loaded the booking (some queries don't select this column).
  useEffect(() => {
    if (!localBooking?.id) return;
    let active = true;
    (async () => {
      const { data } = await supabase
        .from('bookings')
        .select('automations_disabled')
        .eq('id', localBooking.id)
        .maybeSingle();
      if (active && data) {
        setLocalBooking(prev =>
          prev && (prev as any).automations_disabled !== data.automations_disabled
            ? ({ ...prev, automations_disabled: data.automations_disabled } as Booking)
            : prev,
        );
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localBooking?.id]);

  const handleUnscheduleBooking = async () => {
    try {
      setStatusLoading(true);
      setError(null);

      if (!localBooking?.id) {
        throw new Error('No booking ID found');
      }

      // Removing the date means any date-based automations no longer apply.
      if (selectedBusiness?.id) {
        try {
          await cancelBookingAutomations(localBooking.id, 'booking_unscheduled', selectedBusiness.id);
        } catch (error) {
          console.error('[UNSCHEDULE] Error cancelling automations:', error);
        }
      }

      const result = await updateBooking(localBooking.id, {
        is_unscheduled: true,
        status: 'scheduled',
        start_date: null,
        start_time: null,
        end_date: null,
        end_time: null,
        drop_off_date: null,
        drop_off_time: null,
        completed_at: null,
      } as Partial<Booking>);

      if (!result) {
        throw new Error('Failed to unschedule booking');
      }

      setLocalBooking(prev => {
        if (!prev) return null;
        return {
          ...prev,
          is_unscheduled: true,
          status: 'scheduled',
          start_date: undefined,
          start_time: undefined,
          end_date: undefined,
          end_time: undefined,
          drop_off_date: undefined,
          drop_off_time: undefined,
          completed_at: null,
        } as Booking;
      });

      onUpdate();
    } catch (err) {
      console.error('[UNSCHEDULE] Error unscheduling booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to unschedule booking');
      throw err;
    } finally {
      setStatusLoading(false);
    }
  };

  const handleScheduleChange = async (dropOffDate: string, dropOffTime: string, startDate: string, startTime: string, endDate: string, endTime: string) => {
    if (!localBooking?.id) return;

    try {
      setLoading(true);
      setError(null);

      // Validate minimum 30-minute duration for mobile bookings
      if (localBooking.mobile_booking && startDate && startTime && endDate && endTime) {
        const start = new Date(`${startDate}T${startTime}`);
        const end = new Date(`${endDate}T${endTime}`);
        const diffMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
        if (diffMinutes < 30) {
          throw new Error('Complete By time must be at least 30 minutes after the Scheduled time');
        }
      }
      
      console.log('Updating booking schedule:', {
        bookingId: localBooking.id,
        dropOffDate,
        dropOffTime,
        startDate,
        startTime,
        endDate,
        endTime
      });

      // 🚫 Cancel existing automations ONLY if drop-off date/time changed (for location bookings in scheduled status)
      const dropOffDateChanged = localBooking.drop_off_date !== dropOffDate;
      const dropOffTimeChanged = localBooking.drop_off_time !== dropOffTime;
      const shouldCancelAndRetriggerAutomations = selectedBusiness?.id && 
        localBooking.mobile_booking === false && 
        localBooking.status === 'scheduled' &&
        (dropOffDateChanged || dropOffTimeChanged);

      if (shouldCancelAndRetriggerAutomations) {
        try {
          console.log(`[RESCHEDULE] 🎯 Drop-off date/time changed - cancelling and retriggering automations`);
          console.log(`[RESCHEDULE] 📅 Drop-off date changed: ${localBooking.drop_off_date} → ${dropOffDate} (${dropOffDateChanged})`);
          console.log(`[RESCHEDULE] ⏰ Drop-off time changed: ${localBooking.drop_off_time} → ${dropOffTime} (${dropOffTimeChanged})`);
          console.log(`[RESCHEDULE] 🚫 Cancelling existing automations for booking ${localBooking.id}`);
          const cancellationResult = await cancelBookingAutomations(
            localBooking.id,
            'booking_rescheduled',
            selectedBusiness.id
          );
          
          if (cancellationResult.success) {
            console.log(`[RESCHEDULE] ✅ Successfully cancelled ${cancellationResult.cancelledJobs} automation jobs`);
          } else {
            console.warn(`[RESCHEDULE] ⚠️ Automation cancellation had issues:`, cancellationResult.errors);
          }
        } catch (error) {
          console.error('[RESCHEDULE] ❌ Error cancelling automations:', error);
          // Don't fail the reschedule if automation cancellation fails
        }
      } else {
        console.log(`[RESCHEDULE] ⚠️ Skipping automation cancel/retrigger - conditions not met:`, {
          hasBusinessId: !!selectedBusiness?.id,
          isLocationBooking: localBooking.mobile_booking === false,
          isScheduledStatus: localBooking.status === 'scheduled',
          dropOffDateChanged: dropOffDateChanged,
          dropOffTimeChanged: dropOffTimeChanged,
          currentStatus: localBooking.status,
          isMobile: localBooking.mobile_booking
        });
      }
      
      // For mobile bookings, don't include drop_off fields
      const updateData: any = {
        start_date: startDate,
        start_time: startTime,
        end_date: endDate,
        end_time: endTime
      };
      
      // Only include drop_off fields for location bookings (not mobile)
      if (!localBooking.mobile_booking) {
        updateData.drop_off_date = dropOffDate;
        updateData.drop_off_time = dropOffTime;
      }
      
      await updateBooking(localBooking.id, updateData);
      
      // Update local booking state
      const updatedBooking = {
        ...localBooking,
        drop_off_date: dropOffDate,
        drop_off_time: dropOffTime,
        start_date: startDate,
        start_time: startTime,
        end_date: endDate,
        end_time: endTime
      };
      
      setLocalBooking(updatedBooking);
      
      // 🚀 Trigger new automations with updated schedule (ONLY for location bookings in scheduled status)
      if (shouldCancelAndRetriggerAutomations) {
        try {
          console.log(`[RESCHEDULE] 🚀 Triggering new automations for rescheduled location booking (treating as new booking)`);
          
          // Prepare booking data for automation trigger
          const bookingData = {
            booking_id: localBooking.id, // Use actual UUID
            booking_number: localBooking.booking_number,
            title: localBooking.title,
            customer: {
              full_name: `${localBooking.customer?.first_name || ''} ${localBooking.customer?.last_name || ''}`.trim(),
              phone: localBooking.customer?.phone || undefined,
              email: localBooking.customer?.email || undefined
            },
            schedule: {
              drop_off_date: dropOffDate,
              drop_off_time: dropOffTime,
              start_date: startDate,
              start_time: startTime,
              end_date: endDate,
              end_time: endTime
            },
            services: (localBooking.services || []).map(service => ({
              name: service.name,
              price: service.price,
              duration_minutes: service.duration_minutes || 60,
              vehicle: {
                id: service.vehicle_id,
                make: service.vehicle_make,
                model: service.vehicle_model,
                year: service.vehicle_year,
                color: service.vehicle_color
              }
            })),
            grand_total: localBooking.grand_total || localBooking.subtotal || 0,
            resources: (localBooking.resources || []).map(resource => ({
              name: resource.name || `${resource.first_name || ''} ${resource.last_name || ''}`.trim(),
              email: resource.email || '',
              phone: resource.phone || ''
            })),
            vehicles: (localBooking.services || []).map(service => ({
              make: service.vehicle_make || '',
              model: service.vehicle_model || '',
              year: service.vehicle_year || 0,
              color: service.vehicle_color || '',
              display: `${service.vehicle_year || ''} ${service.vehicle_make || ''} ${service.vehicle_model || ''}`.trim()
            })),
            booking_address: localBooking.address || '',
            status: localBooking.status,
            source: 'reschedule_action'
          };
          
          await triggerBookingAutomations(selectedBusiness.id, 'created', bookingData);
          console.log(`[RESCHEDULE] ✅ New automations triggered successfully for location booking`);
        } catch (error) {
          console.error('[RESCHEDULE] ❌ Error triggering new automations:', error);
          // Don't fail the reschedule if automation triggering fails
        }
      } else {
        console.log(`[RESCHEDULE] ⚠️ Skipping automation retrigger - not a location booking in scheduled status`);
      }
      
      // Notify parent component to refresh calendar
      onUpdate();
      
      console.log('Booking schedule updated successfully');
    } catch (err) {
      console.error('Error updating booking schedule:', err);
      setError(err instanceof Error ? err.message : 'Failed to update booking schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleCompletedAtChange = async (completedAt: string) => {
    if (!localBooking?.id) return;

    try {
      setLoading(true);
      setError(null);
      
      console.log('Updating booking completed at timestamp:', {
        bookingId: localBooking.id,
        completedAt
      });
      
      // Update the booking's completed_at timestamp
      await updateBooking(localBooking.id, {
        completed_at: completedAt
      });
      
      // Also update all related flat rate items in payroll_page table
      console.log('Updating payroll flat rate items completed_at timestamp:', {
        bookingId: localBooking.id,
        completedAt
      });
      
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }
      
      const { error: payrollError } = await supabase
        .from('payroll_page')
        .update({ completed_at: completedAt })
        .eq('booking_id', localBooking.id)
        .eq('entry_type', 'flat_rate');
      
      if (payrollError) {
        console.error('Error updating payroll entries:', payrollError);
        throw new Error(`Failed to update payroll entries: ${payrollError.message}`);
      }
      
      // Update local booking state
      setLocalBooking(prev => prev ? {
        ...prev,
        completed_at: completedAt
      } : null);
      
      // Notify parent component to refresh calendar
      onUpdate();
      
      console.log('Booking and payroll flat rate items completed at timestamp updated successfully');
    } catch (err) {
      console.error('Error updating booking completed at timestamp:', err);
      setError(err instanceof Error ? err.message : 'Failed to update completion timestamp');
    } finally {
      setLoading(false);
    }
  };



  const handleDeactivate = async () => {
    if (!localBooking) return;

    try {
      setLoading(true);
      setError(null);
      
      await deleteBooking(localBooking.id);
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error deactivating booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to deactivate booking');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteAllWorkflowSteps = async () => {
    if (!localBooking?.id) return;
    
    try {
      console.log('[WORKFLOW MODAL] Completing all workflow steps...');
      await completeAllWorkflowSteps(localBooking.id);
      console.log('[WORKFLOW MODAL] Successfully completed all workflow steps');
      // Refresh booking data
      await loadBookingData(localBooking.id);
    } catch (error) {
      console.error('[WORKFLOW MODAL] Error completing workflow steps:', error);
      throw error; // Re-throw to let WorkflowCompletionModal handle it
    }
  };

  const handleCompleteAllFlatRateGroups = async () => {
    if (!localBooking?.id) return;
    
    try {
      console.log('[FLAT RATE MODAL] Completing all flat rate groups...');
      await completeAllFlatRateGroups(localBooking.id);
      console.log('[FLAT RATE MODAL] Successfully completed all flat rate groups');
      // Refresh booking data
      await loadBookingData(localBooking.id);
    } catch (error) {
      console.error('[FLAT RATE MODAL] Error completing flat rate groups:', error);
      throw error; // Re-throw to let FlatRateCompletionModal handle it
    }
  };

  const handleProceedWithFlatRateCompletion = async () => {
    if (!localBooking?.id || !pendingStatusChange) return;
    
    setIsFlatRateCompletionModalOpen(false);
    
    // Continue with the original status change
    try {
      setStatusLoading(true);
      setError(null);

      // Update completed_at timestamp for completed bookings
      let completed_at = localBooking.completed_at;
      if (pendingStatusChange === 'completed' && !completed_at) {
        completed_at = new Date().toISOString();
      }

      const result = await updateBooking(localBooking.id, {
        status: pendingStatusChange,
        completed_at
      });

      if (!result) {
        throw new Error('Failed to update booking status');
      }

      // Update local state
      setLocalBooking(prev => {
        if (!prev) return null;
        return {
          ...prev,
          status: pendingStatusChange,
          completed_at: completed_at || prev.completed_at
        } as Booking;
      });

      // Reset pending status
      setPendingStatusChange(null);

      console.log(`[FLAT RATE COMPLETION] Booking status updated to ${pendingStatusChange}`);
      onUpdate();
    } catch (error) {
      console.error('[FLAT RATE COMPLETION] Error updating booking status:', error);
      setError(error instanceof Error ? error.message : 'Failed to complete booking');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleProceedWithCompletion = async () => {
    if (!localBooking?.id || !pendingStatusChange) return;
    
    setIsWorkflowCompletionModalOpen(false);
    
    // Continue with the original status change
    try {
      setStatusLoading(true);
      setError(null);

      // Update completed_at timestamp for completed bookings
      let completed_at = localBooking.completed_at;
      if (pendingStatusChange === 'completed' && !completed_at) {
        completed_at = new Date().toISOString();
      }

      const result = await updateBooking(localBooking.id, {
        status: pendingStatusChange,
        completed_at
      });

      if (!result) {
        throw new Error('Failed to update booking status');
      }

      // Update local state
      setLocalBooking(prev => {
        if (!prev) return null;
        return {
          ...prev,
          status: pendingStatusChange,
          completed_at: completed_at || prev.completed_at,
        } as Booking;
      });

      // Create booking completed milestone
      if (pendingStatusChange === 'completed') {
        try {
          await createBookingCompletedMilestone(
            localBooking.business_id,
            localBooking.customer?.id || '',
            localBooking.id,
            localBooking.title,
            completed_at
          );
        } catch (milestoneError) {
          console.error('Error creating booking completed milestone:', milestoneError);
        }
      }

      // Process status change for automations
      if (pendingStatusChange === 'in_progress' || pendingStatusChange === 'completed') {
        try {
          await processBookingStatusChange(localBooking.id, pendingStatusChange, localBooking.status);
        } catch (error) {
          console.error('Error processing booking status change:', error);
        }
      }

      // Trigger AutomationsV2
      await triggerBookingAutomations(localBooking.id, pendingStatusChange, localBooking.status);

      onUpdate();
    } catch (err) {
      console.error('Error updating booking status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update booking status');
    } finally {
      setStatusLoading(false);
      setPendingStatusChange(null);
    }
  };

  const handleColorUpdate = async (color: string, secondaryColor?: string) => {
    if (!localBooking?.id) return;

    try {
      setLoading(true);
      setError(null);

      // Update the booking color in the database
      await updateBooking(localBooking.id, {
        color: color,
        secondary_color: secondaryColor || null
      });

      // Update local state immediately
      setLocalBooking(prev => prev ? {
        ...prev,
        color: color,
        secondary_color: secondaryColor || null
      } : null);

      // Refresh the calendar/list view
      onUpdate();

      // Close the booking color picker modal
      setIsBookingColorPickerModalOpen(false);
    } catch (err) {
      console.error('Error updating booking color:', err);
      setError(err instanceof Error ? err.message : 'Failed to update booking color');
    } finally {
      setLoading(false);
    }
  };

  // Listen for global event to close modal
  useEffect(() => {
    const handleCloseAllModals = () => {
      if (isOpen) {
        onClose();
      }
    };

    window.addEventListener('closeAllModals', handleCloseAllModals);
    
    return () => {
      window.removeEventListener('closeAllModals', handleCloseAllModals);
    };
  }, [isOpen, onClose]);

  // Resolve the effective z-index from the modal stack: when this modal is opened
  // *inside* another modal (e.g. CustomerMessageModal's milestone view), it auto-
  // elevates above that parent; standalone it keeps the default. No hardcoded
  // z-index bumping required.
  const resolvedZIndex = useModalZIndex(zIndex);

  if (!isOpen && !isModalVisible) return null;
  if (!localBooking) return null;

  const effectiveZIndex = resolvedZIndex;
  const isElevated = effectiveZIndex !== 99999;

  // Modal content to render.
  // Wrapped in ModalStackProvider so that any nested modals/menus that opt into
  // the stack (via useModalZIndex) automatically render above this modal even
  // when it is itself elevated (e.g. opened from CustomerDetailsModal).
  const modalContent = (
    <ModalStackProvider zIndex={effectiveZIndex}>
    <div
      className={`fixed inset-0 z-[9999] booking-info-modal ios-modal-fix ${isElevated ? 'booking-info-modal-elevated' : ''} ${isModalVisible ? '' : 'pointer-events-none'}`}
      aria-modal="true"
      role="dialog"
      style={{
        // REMOVED: Add will-change to hint browser to use hardware acceleration - was causing flash
        // willChange: 'transform, opacity',
        // Force modal to be on top with a higher z-index (overridable when opened
        // above another high-z modal). Inner layers are scoped via isolation below.
        // The CSS .ios-modal-fix sets z-index !important; when elevated we win via
        // the higher-specificity .booking-info-modal-elevated rule + this var.
        zIndex: effectiveZIndex,
        ...(isElevated ? ({ ['--bim-elevated-z' as any]: effectiveZIndex }) : {}),
        // REMOVED: Force 3D rendering to create new stacking context - was causing flash
        // transform: 'translateZ(0) scale(1.0, 1.0)',
        // Ensure modal is rendered on top of everything
        isolation: 'isolate',
        // iOS specific overrides
        WebkitOverflowScrolling: 'touch',
        // REMOVED: WebkitTransform - was causing flash
        // WebkitTransform: 'translateZ(0) scale(1.0, 1.0)',
        WebkitBackfaceVisibility: 'hidden',
        // Position relative to viewport for iOS; respect safe area (notch / home indicator)
        position: 'fixed',
        top: 'env(safe-area-inset-top, 0px)',
        left: 'env(safe-area-inset-left, 0px)',
        right: 'env(safe-area-inset-right, 0px)',
        bottom: 'env(safe-area-inset-bottom, 0px)',
        height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
        width: 'calc(100% - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))'
      }}
    >
      <div 
        className={`fixed inset-0 z-[99900] ios-modal-backdrop ${
          isModalVisible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          position: 'fixed',
          zIndex: 99900,
          top: 'env(safe-area-inset-top, 0px)',
          left: 'env(safe-area-inset-left, 0px)',
          right: 'env(safe-area-inset-right, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          pointerEvents: isModalVisible ? 'auto' : 'none'
        }}
      >
        <div 
          className={`fixed inset-0 ios-modal-content transition-colors duration-200 ${
            isModalVisible ? 'bg-gray-50' : 'bg-black'
          }`}
          style={{ 
            WebkitBackfaceVisibility: 'hidden',
            zIndex: 99950,
            position: 'fixed',
            top: 'env(safe-area-inset-top, 0px)',
            left: 'env(safe-area-inset-left, 0px)',
            right: 'env(safe-area-inset-right, 0px)',
            bottom: 'env(safe-area-inset-bottom, 0px)',
            width: 'calc(100% - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))',
            height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            display: isOpen ? 'block' : 'none'
          }}
        >
          <div className={`h-full flex flex-col transition-opacity duration-200 ${
            isModalVisible ? 'opacity-100' : 'opacity-0'
          }`} style={{ 
            paddingTop: 0,
            position: 'relative',
            zIndex: 9999
          }}>
            <BookingHeader
              overlayBaseZIndex={isElevated ? effectiveZIndex + 60 : undefined}
              bookingNumber={localBooking.booking_number || 0}
              title={localBooking.title || 'Untitled Booking'}
              status={localBooking.status}
              onClose={onClose}
              onEdit={undefined}
              onEditColor={undefined}
              onScheduleFieldQuote={createFqFromBookings ? handleOpenScheduleFieldQuote : undefined}
              bookingColor={localBooking.color}
              bookingSecondaryColor={localBooking.secondary_color}
              onUpdate={refreshBooking}
              bookingCreatedBy={localBooking.created_by}
              automationsDisabled={(localBooking as any).automations_disabled ?? false}
              onToggleAutomationsDisabled={handleToggleAutomationsDisabled}
              onDelete={async () => {
                try {
                  if (!localBooking?.id) {
                    throw new Error('No booking ID found');
                  }
                  
                  console.log('Attempting to delete booking:', {
                    bookingId: localBooking.id,
                    bookingNumber: localBooking.booking_number
                  });

                  // 🚫 Cancel existing automations BEFORE deleting the booking
                  if (selectedBusiness?.id) {
                    try {
                      console.log(`[DELETE] 🚫 Cancelling existing automations for booking ${localBooking.id}`);
                      const cancellationResult = await cancelBookingAutomations(
                        localBooking.id,
                        'booking_deleted',
                        selectedBusiness.id
                      );
                      
                      if (cancellationResult.success) {
                        console.log(`[DELETE] ✅ Successfully cancelled ${cancellationResult.cancelledJobs} automation jobs`);
                      } else {
                        console.warn(`[DELETE] ⚠️ Automation cancellation had issues:`, cancellationResult.errors);
                      }
                    } catch (error) {
                      console.error('[DELETE] ❌ Error cancelling automations:', error);
                      // Don't fail the booking deletion if automation cancellation fails
                    }
                  }

                  // 🎯 Trigger NEW QStash-based automations BEFORE deleting the booking
                  if (selectedBusiness?.id && localBooking) {
                    try {
                      console.log(`[DELETE] 🚀 Triggering NEW QStash automations for deleted booking`);
                      
                      // Prepare booking data for automation trigger
                      const realBookingData = {
                        booking_id: localBooking.id,
                        booking_number: localBooking.booking_number,
                        title: localBooking.title,
                        customer: {
                          full_name: `${localBooking.customer?.first_name || ''} ${localBooking.customer?.last_name || ''}`.trim(),
                          phone: localBooking.customer?.phone || undefined,
                          email: localBooking.customer?.email || undefined
                        },
                        schedule: {
                          drop_off_date: localBooking.drop_off_date || '',
                          drop_off_time: localBooking.drop_off_time || '',
                          start_date: localBooking.start_date || '',
                          start_time: localBooking.start_time || '',
                          end_date: localBooking.end_date || '',
                          end_time: localBooking.end_time || ''
                        },
                        services: (localBooking.services || []).map(service => ({
                          name: service.name,
                          price: service.price,
                          duration_minutes: service.duration_minutes || 60,
                          vehicle: {
                            make: service.vehicle_make || '',
                            model: service.vehicle_model || '',
                            year: service.vehicle_year || 0,
                            color: service.vehicle_color || ''
                          }
                        })),
                        vehicles: (() => {
                          const vehicles = (localBooking.services || [])
                            .filter(service => service.vehicle_make || service.vehicle_model)
                            .map(service => ({
                              make: service.vehicle_make || '',
                              model: service.vehicle_model || '', 
                              year: service.vehicle_year || 0,
                              color: service.vehicle_color || '',
                              display: `${service.vehicle_year || ''} ${service.vehicle_make || ''} ${service.vehicle_model || ''}`.trim()
                            }));
                          
                          // Remove duplicates based on make/model/year combination
                          const uniqueVehicles = vehicles.filter((vehicle, index, self) => 
                            index === self.findIndex(v => v.make === vehicle.make && v.model === vehicle.model && v.year === vehicle.year)
                          );
                          
                          return uniqueVehicles;
                        })(),
                        grand_total: (localBooking as any).grand_total || localBooking.subtotal || 0,
                        status: 'deleted',
                        resources: (localBooking.resources || []).map(bookingResource => ({
                          name: `${bookingResource.resource?.first_name || ''} ${bookingResource.resource?.last_name || ''}`.trim(),
                          email: bookingResource.resource?.email || '',
                          phone: bookingResource.resource?.phone || ''
                        })),
                        source: 'delete_action'
                      };

                      console.log(`🎯 DELETE: Triggering QStash automations with data:`, realBookingData);
                      await triggerBookingAutomations(selectedBusiness.id, 'deleted', realBookingData);
                      console.log(`🎯 DELETE: QStash automations triggered successfully for deleted booking`);
                    } catch (automationError) {
                      console.error(`❌ DELETE: Failed to trigger QStash automations:`, automationError);
                      // Don't fail the entire deletion process if automation fails
                    }
                  }
                  
                  await deleteBooking(localBooking.id);
                  
                  console.log('Booking deleted successfully');
                  onUpdate();
                  onClose();
                } catch (err) {
                  console.error('Error deleting booking:', err);
                  const errorMessage = err instanceof Error ? err.message : 'Failed to delete booking';
                  setError(errorMessage);
                  throw new Error(errorMessage);
                }
              }}
              onCancel={handleCancelBooking}
              onUnschedule={!localBooking.is_unscheduled ? handleUnscheduleBooking : undefined}
              
              completedAt={localBooking.completed_at}
              onStatusChange={localBooking.status === 'cancelled' ? undefined : handleStatusChange}
              onCompletedAtChange={handleCompletedAtChange}
              readOnly={readOnly || localBooking.status === 'cancelled'}
              isMobile={isMobile}
              bookingId={localBooking.id}
              businessId={localBooking.business_id}
              canEdit={canAccess.editBookingDetails}
              // Damage and waiver props
              isLiabilityWaiverEnabled={damageWaiverState.isLiabilityWaiverEnabled}
              liabilityWaiverAgreed={damageWaiverState.liabilityWaiverAgreed}
              hasDamageReport={damageWaiverState.hasDamageReport}
              onSendLiabilityWaiver={damageWaiverState.onSendLiabilityWaiver}
              onAddDamageReport={damageWaiverState.onAddDamageReport}
              isSendingLiabilityWaiver={damageWaiverState.isSendingLiabilityWaiver}
              liabilityWaiverPdfUrl={damageWaiverState.liabilityWaiverPdfUrl}
              canAccessLiabilityWaiver={canAccess.bookingLiabilityWaiver}
              onCreateWarranty={() => {
                console.log('Create Warranty clicked');
                
                // Store warranty data in localStorage before closing the modal
                if (localBooking) {
                  const warrantyData = {
                    originalBookingId: localBooking.id,
                    originalBookingNumber: localBooking.booking_number,
                    customer: localBooking.customer,
                    vehicle: localBooking.services?.[0] ? {
                      id: localBooking.services[0].vehicle_id,
                      make: localBooking.services[0].vehicle_make,
                      model: localBooking.services[0].vehicle_model,
                      year: localBooking.services[0].vehicle_year,
                      color: localBooking.services[0].vehicle_color
                    } : null,
                    timestamp: Date.now()
                  };
                  localStorage.setItem('warrantyBookingData', JSON.stringify(warrantyData));
                  console.log('🛡️ Warranty data stored:', warrantyData);
                  console.log('🛡️ Original booking number:', localBooking.booking_number);
                  console.log('🛡️ Full localBooking object:', localBooking);
                  console.log('🛡️ Booking number type:', typeof localBooking.booking_number);
                }
                
                // Close the BookingInfoModal and dispatch event to open warranty modal
                onClose();
                window.dispatchEvent(new CustomEvent('openWarrantyModal', {
                  detail: {
                    originalBookingId: localBooking.id
                  }
                }));
              }}
              linkedProject={linkedProject}
              onViewProject={() => {
                if (linkedProject?.id) {
                  // Close this modal first
                  onClose();
                  // Navigate to Projects page and open the project modal
                  navigate('/projects', { 
                    state: { openProjectId: linkedProject.id } 
                  });
                }
              }}
              linkedOpportunity={linkedOpportunity}
              onViewOpportunity={() => {
                if (linkedOpportunity?.id) {
                  // Close this modal first
                  onClose();
                  // Navigate to CRM page and open the opportunity modal
                  navigate('/crm', { 
                    state: { openOpportunityId: linkedOpportunity.id } 
                  });
                }
              }}
            />

                        {/* Conditional rendering based on mobile/desktop */}
            {isMobile ? (
              <>
                <MobileTabStatusHeader
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  isInventoryAppInstalled={isInventoryAppInstalled}
                  showMaterialsInBookings={inventorySettingsLoaded ? showMaterialsInBookings : false}
                  isExpensesAppInstalled={isExpensesAppInstalled}
                  isPayrollAppInstalled={isPayrollAppInstalled}
                  showFlatRateInBookings={payrollSettingsLoaded ? showFlatRateInBookings : false}
                  hasFieldQuote={hasFieldQuote}
                />
                {/* Mobile Completed Info - Underneath Tab Selector */}
                {localBooking.status === 'completed' && localBooking.completed_at && (
                  <div className="backdrop-blur-xl bg-white/80 border-b border-gray-200 px-3 py-2"
                       style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                    <div className="flex items-center justify-center space-x-2 text-xs text-gray-600">
                      <Clock className="h-3 w-3" />
                      <span>
                        {`Completed on ${format(new Date(localBooking.completed_at), 'MMM d, yyyy')}`}
                      </span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <BookingStatusBar
                  status={localBooking.status}
                  createdAt={localBooking.created_at}
                  createdBy={localBooking.created_by}
                  completedAt={localBooking.completed_at}
                  onStatusChange={localBooking.status === 'cancelled' ? undefined : handleStatusChange}
                  onCompletedAtChange={handleCompletedAtChange}
                  readOnly={readOnly || localBooking.status === 'cancelled'}
                  bookingId={localBooking.id}
                  businessId={localBooking.business_id}
                  canEdit={canAccess.editBookingDetails}
                />

                <BookingTabs 
                  activeTab={activeTab} 
                  onTabChange={setActiveTab}
                  isInventoryAppInstalled={isInventoryAppInstalled}
                  showMaterialsInBookings={inventorySettingsLoaded ? showMaterialsInBookings : false}
                  isExpensesAppInstalled={isExpensesAppInstalled}
                  isPayrollAppInstalled={isPayrollAppInstalled}
                  showFlatRateInBookings={payrollSettingsLoaded ? showFlatRateInBookings : false}
                  hasFieldQuote={hasFieldQuote}
                />
                
                {/* Debug: Log the actual props being passed */}
                {console.log('🔍 FINAL Props to BookingTabs:', {
                  isPayrollAppInstalled,
                  payrollSettingsLoaded,
                  showFlatRateInBookings,
                  finalShowFlatRate: payrollSettingsLoaded ? showFlatRateInBookings : false
                })}
              </>
            )}

            {/* Content area */}
            <div 
              className="flex-1 overflow-y-auto relative min-h-0" 
              style={{ 
                WebkitOverflowScrolling: 'touch',
                overflowX: 'hidden'
              }}
            >
              <div 
                className="max-w-5xl mx-auto py-1 px-2 sm:py-6 sm:px-6" 
                style={{
                  paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
                }}
              >
                {error && (
                  <div className="mb-3 sm:mb-6 backdrop-blur-xl bg-red-50/90 border border-red-200/50 text-red-700 px-3 py-2 sm:px-4 sm:py-3 rounded-xl sm:rounded-2xl text-xs sm:text-sm shadow-sm"
                       style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                    {error}
                  </div>
                )}

                {isEditMode ? (
                  <div className="space-y-6">
                    {/* Title Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Booking Title
                      </label>
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="w-full h-12 px-4 rounded-xl border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 text-base"
                        placeholder="Enter booking title"
                        autoFocus
                      />
                    </div>

                    {/* Color Picker */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Booking Color
                      </label>
                      <div className="flex items-center gap-4">
                        {/* Current Color Display */}
                        <div className="flex items-center gap-3">
                          <div className="w-16 h-16 rounded-xl border-2 border-gray-300 overflow-hidden shadow-sm">
                            {editSecondaryColor ? (
                              <div className="w-full h-full flex">
                                <div 
                                  className="w-1/2 h-full"
                                  style={{ backgroundColor: editColor }}
                                />
                                <div 
                                  className="w-1/2 h-full"
                                  style={{ backgroundColor: editSecondaryColor }}
                                />
                              </div>
                            ) : (
                              <div 
                                className="w-full h-full"
                                style={{ backgroundColor: editColor }}
                              />
                            )}
                          </div>
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-600">Primary</span>
                            <input
                              type="color"
                              value={editColor}
                              onChange={(e) => setEditColor(e.target.value)}
                              className="w-12 h-8 rounded border border-gray-300 cursor-pointer"
                            />
                          </div>
                          {editSecondaryColor && (
                            <div className="flex flex-col">
                              <span className="text-sm text-gray-600">Secondary</span>
                              <input
                                type="color"
                                value={editSecondaryColor}
                                onChange={(e) => setEditSecondaryColor(e.target.value)}
                                className="w-12 h-8 rounded border border-gray-300 cursor-pointer"
                              />
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (editSecondaryColor) {
                              setEditSecondaryColor(undefined);
                            } else {
                              setEditSecondaryColor('#ffffff');
                            }
                          }}
                          className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          {editSecondaryColor ? 'Remove Secondary' : 'Add Secondary'}
                        </button>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            setLoading(true);
                            setError(null);
                            await updateBooking(localBooking.id, {
                              title: editTitle.trim(),
                              color: editColor,
                              secondary_color: editSecondaryColor || null
                            });
                            await refreshBooking();
                            setIsEditMode(false);
                          } catch (err) {
                            console.error('Error updating booking:', err);
                            setError(err instanceof Error ? err.message : 'Failed to update booking');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={loading}
                        className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditMode(false);
                          setEditTitle(localBooking?.title || '');
                          setEditColor(localBooking?.color || '#3b82f6');
                          setEditSecondaryColor(localBooking?.secondary_color || undefined);
                        }}
                        disabled={loading}
                        className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <BookingContent
                  activeTab={activeTab}
                  onFieldQuotesChanged={refreshHasFieldQuote}
                  booking={localBooking}
                  isSelectMaterialModalOpen={isSelectMaterialModalOpen}
                  setIsSelectMaterialModalOpen={setIsSelectMaterialModalOpen}
                  isExpensesAppInstalled={isExpensesAppInstalled}
                  onEditCustomer={() => setIsCustomerDetailsModalOpen(true)}
                  onChangeCustomer={() => setIsSelectCustomerModalOpen(true)}
                  onEditVehicle={async (vehicleId, opts) => {
                    setScannedVinForEdit(opts?.scannedVin || null);
                    try {
                      const fullVehicle = await getCustomerVehicle(vehicleId);
                      setSelectedVehicle(fullVehicle);
                      setIsEditVehicleModalOpen(true);
                    } catch (err) {
                      console.error('Error loading vehicle for edit:', err);
                      const service = localBooking.services?.find(s => s.vehicle_id === vehicleId);
                      if (service) {
                        setSelectedVehicle({
                          id: vehicleId,
                          customer_id: localBooking.customer?.id || '',
                          make: service.vehicle_make || '',
                          model: service.vehicle_model || '',
                          year: service.vehicle_year || new Date().getFullYear(),
                          color: service.vehicle_color || '',
                          vin: null,
                          license_plate: null,
                          mileage: null,
                          notes: null,
                          is_primary: false,
                          last_service: null,
                          created_at: '',
                          updated_at: '',
                        } satisfies CustomerVehicle);
                        setIsEditVehicleModalOpen(true);
                      }
                    }
                  }}
                  onAddService={(vehicleId) => {
                    setCurrentVehicleId(vehicleId);
                    if (vehicleId) {
                      const vehicle = localBooking.services?.find(s => s.vehicle_id === vehicleId);
                      if (vehicle) {
                        setCurrentVehicle({
                          id: vehicleId,
                          make: vehicle.vehicle_make,
                          model: vehicle.vehicle_model,
                          year: vehicle.vehicle_year,
                          color: vehicle.vehicle_color
                        });
                      }
                    }
                    setIsSelectServiceModalOpen(true);
                  }}
                  onAddProposal={isProposalsAppInstalled && canAccess.proposals ? () => setIsCreateProposalFromBookingOpen(true) : undefined}
                  bookingProposals={canAccess.proposals ? bookingProposals : []}
                  onViewProposal={canAccess.proposals ? async (proposal) => {
                    try {
                      const fullProposal = await getProposal(proposal.id);
                      setSelectedProposalForView(fullProposal);
                      setIsProposalDetailsOpen(true);
                    } catch (err) {
                      console.error('Error loading proposal:', err);
                    }
                  } : undefined}
                  onRemoveResource={handleRemoveResource}
                  onAddResource={() => setIsSelectResourceModalOpen(true)}
                  assetsEnabled={assetsEnabled}
                  onRemoveAsset={handleRemoveAsset}
                  onAddAsset={() => setIsSelectResourceModalOpen(true)}
                  onApplyDiscount={() => setIsApplyDiscountModalOpen(true)}
                  onDeleteDiscount={async (discountId) => {
                    try {
                      setLoading(true);
                      setError(null);
                      await deleteDiscount(discountId);
                      await refreshBooking();
                    } catch (err) {
                      console.error('Error removing discount:', err);
                      setError(err instanceof Error ? err.message : 'Failed to remove discount');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  onUpdate={refreshBooking}
                  onCloseBooking={onClose}
                  onError={setError}
                  onAddressUpdate={handleAddressUpdate}
                  onRescheduleMobileBooking={onRescheduleMobileBooking}
                  onStatusChange={handleStatusChange}
                  onScheduleChange={handleScheduleChange}
                  onDamageWaiverStateChange={setDamageWaiverState}
                  onViewInvoiceFromHeader={() => setIsBetaPaymentsModalOpen(true)}
                  onSwitchToBooking={handleSwitchToBooking}
                />
                )}
              </div>
            </div>

            {/* Sticky Payment Summary Footer — mobile only, overview tab */}
            {isMobile && activeTab === 'overview' && localBooking && canAccess.bookingSalesInfo && (
              <BookingPaymentSummary
                compact
                subtotal={localBooking.subtotal || 0}
                discountAmount={localBooking.discount_amount}
                discounts={localBooking.discounts as any}
                taxAmount={localBooking.tax_amount || 0}
                totalAmount={localBooking.total_amount || 0}
                payments={localBooking.payments as any}
                lineItems={localBooking.services as any}
                businessId={localBooking.business_id || ''}
                bookingId={localBooking.id}
                onApplyDiscount={() => setIsApplyDiscountModalOpen(true)}
                onDeleteDiscount={async (discountId) => {
                  try {
                    setLoading(true);
                    setError(null);
                    await deleteDiscount(discountId, localBooking.id);
                    await refreshBooking();
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to delete discount');
                  } finally {
                    setLoading(false);
                  }
                }}
                onRecordPayment={() => setIsRecordPaymentModalOpen(true)}
                onViewInvoice={() => setIsBetaPaymentsModalOpen(true)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Record Payment Modal (for sticky footer) */}
      {localBooking && (
        <RecordPaymentModal
          isOpen={isRecordPaymentModalOpen}
          onClose={() => setIsRecordPaymentModalOpen(false)}
          onSuccess={async () => {
            await refreshBooking();
            onUpdate();
          }}
          onSendInvoice={() => {
            setIsBetaPaymentsModalOpen(true);
          }}
          bookingId={localBooking.id}
          businessId={localBooking.business_id || ''}
          bookingTotal={liveInvoice ? liveInvoice.total_cents / 100 : (localBooking.total_amount || 0)}
          amountPaid={
            liveInvoice
              ? liveInvoice.amount_paid_cents / 100
              : localBooking.payments?.reduce(
                  (sum: number, payment: any) =>
                    isPaymentCountedForBalance(payment.status)
                      ? sum + paymentPrincipal(payment)
                      : sum,
                  0,
                ) || 0
          }
          customerId={localBooking.customer?.id}
        />
      )}

      {/* Modals */}
      <SelectCustomerModal
        isOpen={isSelectCustomerModalOpen}
        onClose={() => setIsSelectCustomerModalOpen(false)}
        onSelect={async (customer) => {
          if (!localBooking?.id) return;
          
          try {
            // Update the booking with the new customer
            await updateBooking(localBooking.id, {
              customer_id: customer.id
            });
            
            // Refresh booking data
            await loadBookingData(localBooking.id);
            onUpdate();
            setIsSelectCustomerModalOpen(false);
          } catch (error) {
            console.error('Error changing customer:', error);
            setError('Failed to change customer. Please try again.');
          }
        }}
      />

      <CustomerDetailsModal
        isOpen={isCustomerDetailsModalOpen}
        onClose={() => {
          setIsCustomerDetailsModalOpen(false);
          if (localBooking?.id) {
            loadBookingData(localBooking.id);
          }
        }}
        customer={localBooking?.customer || null}
        onUpdate={() => {
          if (localBooking?.id) {
            loadBookingData(localBooking.id);
          }
          onUpdate();
        }}
      />

      <BetaPaymentsModal
        isOpen={isBetaPaymentsModalOpen}
        onClose={() => setIsBetaPaymentsModalOpen(false)}
        bookingId={localBooking?.id || ''}
        bookingNumber={typeof localBooking?.booking_number === 'number' ? localBooking.booking_number : parseInt(String(localBooking?.booking_number)) || 0}
        bookingData={{
          total_amount: localBooking?.total_amount || 0,
          customer: localBooking?.customer ? {
            id: localBooking.customer.id,
            first_name: localBooking.customer.first_name || '',
            last_name: localBooking.customer.last_name || '',
            email: localBooking.customer.email || undefined,
            phone: localBooking.customer.phone || undefined,
            full_name: `${localBooking.customer.first_name || ''} ${localBooking.customer.last_name || ''}`.trim() || undefined
          } : undefined,
          payments: localBooking?.payments?.map(p => ({
            id: p.id,
            amount: p.amount,
            tip_amount: (p as any).tip_amount || undefined,
            base_amount: (p as any).base_amount || undefined,
            surcharge_amount: (p as any).surcharge_amount || undefined,
            method: (p as any).method || undefined,
            payment_type: (p as any).payment_type || undefined,
            status: p.status,
            pdf_url: (p as any).pdf_url,
            created_at: p.created_at,
            reference: (p as any).reference || undefined,
            stripe_payment_intent_id: (p as any).stripe_payment_intent_id || undefined,
            stripe_checkout_session_id: (p as any).stripe_checkout_session_id || undefined,
            notes: (p as any).notes || undefined
          })) || []
        }}
        booking={localBooking}
        onUpdate={refreshBooking}
      />

      <SelectResourceModal
        isOpen={isSelectResourceModalOpen}
        onClose={() => setIsSelectResourceModalOpen(false)}
        onConfirm={async ({ resources, assets }) => {
          if (!localBooking?.id) return;

          const currentResourceIds = new Set(
            (localBooking.resources || []).map(r => r.resource_id).filter(Boolean) as string[]
          );
          const nextResourceIds = new Set(resources.map(r => r.id));
          const toAddResources = resources.filter(r => !currentResourceIds.has(r.id));

          for (const resource of toAddResources) {
            const conflicts = await checkForConflicts(resource.id, 'resource');
            if (conflicts.length > 0) {
              setConflictWarning({ type: 'resource', item: resource, conflicts });
              return;
            }
          }

          const kept = (localBooking.resources || [])
            .filter(r => r.resource_id && nextResourceIds.has(r.resource_id))
            .map(r => ({
              resource_id: r.resource_id,
              role: r.role,
              service_id: r.service_id,
              start_date: r.start_date,
              end_date: r.end_date,
            }));

          const added = toAddResources.map(r => ({
            resource_id: r.id,
            role: 'technician' as const,
            service_id: null,
            start_date: null,
            end_date: null,
          }));

          const resourcesChanged =
            kept.length !== (localBooking.resources || []).length ||
            toAddResources.length > 0;

          if (resourcesChanged) {
            try {
              setLoading(true);
              await updateBooking(localBooking.id, { resources: [...kept, ...added] });
              setSelectedResources([...nextResourceIds]);
              await refreshBooking();
            } catch (err) {
              console.error('Error updating resources:', err);
              setError(err instanceof Error ? err.message : 'Failed to update resources');
              setLoading(false);
              return;
            } finally {
              setLoading(false);
            }
          }

          if (assetsEnabled) {
            const currentAssetIds = new Set(
              (localBooking.booking_assets || []).map(a => a.asset_id)
            );
            const nextAssetIds = new Set(assets.map(a => a.id));

            for (const ba of localBooking.booking_assets || []) {
              if (!nextAssetIds.has(ba.asset_id)) {
                await handleRemoveAsset(ba.asset_id);
              }
            }
            for (const asset of assets) {
              if (!currentAssetIds.has(asset.id)) {
                const conflicts = await checkForConflicts(asset.id, 'asset');
                if (conflicts.length > 0) {
                  setConflictWarning({ type: 'asset', item: asset, conflicts });
                  return;
                }
                await confirmAddAsset(asset);
              }
            }
          }
        }}
        selectedResources={selectedResources}
        selectedAssetIds={localBooking?.booking_assets?.map(a => a.asset_id) || []}
        assetsEnabled={assetsEnabled}
        confirmLabel="Add to booking"
      />

      <EditVehicleModal
        isOpen={isEditVehicleModalOpen}
        onClose={() => {
          setIsEditVehicleModalOpen(false);
          setScannedVinForEdit(null);
          // Refresh booking data after vehicle update
          if (localBooking?.id) {
            loadBookingData(localBooking.id);
          }
        }}
        vehicle={selectedVehicle}
        onUpdate={refreshBooking}
        initialScannedVin={scannedVinForEdit}
      />

      <SelectVehicleModal
        isOpen={isSelectVehicleModalOpen}
        onClose={() => setIsSelectVehicleModalOpen(false)}
        onSelect={async (vehicle) => {
          setCurrentVehicle(vehicle);
          setCurrentVehicleId(vehicle.id);
          setIsSelectVehicleModalOpen(false);
          setIsSelectServiceModalOpen(true);
        }}
        vehicles={localBooking.customer?.vehicles || []}
        selectedVehicles={localBooking.services?.map(s => ({
          id: s.vehicle_id,
          make: s.vehicle_make || '',
          model: s.vehicle_model || '',
          year: s.vehicle_year || new Date().getFullYear()
        })) || []}
        onAddVehicle={() => {
          setIsSelectVehicleModalOpen(false);
          setCurrentVehicleId(null);
          setCurrentVehicle(null);
          setIsCreateVehicleModalOpen(true);
        }}
      />

      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => setIsSelectServiceModalOpen(false)}
        onAdd={async (service, priceOption) => {
          try {
            if (!localBooking?.id || !currentVehicleId) {
              throw new Error('Invalid booking or vehicle');
            }

            // Calculate tax amounts with proper rounding for currency
            const taxRate = service.tax_exempt ? 0 : 5; // Default tax rate
            const taxAmount = service.tax_exempt ? 0 : Math.round((priceOption.price * taxRate / 100) * 100) / 100;
            const totalAmount = Math.round((priceOption.price + taxAmount) * 100) / 100;

            const pricingType = priceOption.pricing_type || 'flat';
            const isPerUnit = pricingType === 'per_unit' || pricingType === 'per_sqft';

            // Insert new service directly into booking_services table
            const { error: serviceError } = await supabase
              .from('booking_services')
              .insert({
                business_id: selectedBusiness?.id,
                booking_id: localBooking.id,
                service_id: service.id,
                vehicle_id: currentVehicleId,
                name: service.name,
                description: service.description || '',
                price: isPerUnit ? 0 : priceOption.price,
                tax_exempt: service.tax_exempt || false,
                tax_rate: taxRate,
                tax_amount: isPerUnit ? 0 : taxAmount,
                total_amount: isPerUnit ? 0 : totalAmount,
                duration_minutes: isPerUnit ? 0 : priceOption.duration_minutes,
                status: 'pending',
                pricing_type: pricingType,
                unit_price: isPerUnit ? (priceOption.unit_price || 0) : null,
                duration_per_unit: isPerUnit ? (priceOption.duration_per_unit || 0) : null,
                quantity: isPerUnit ? 0 : 1,
                compare_at_price: (priceOption as any).compare_at_price ?? null,
                ...upsellLineStamp(staffResourceId(currentResource)),
              });

            if (serviceError) throw serviceError;

            // Note: Removed PDF generation code - no longer using the old invoicing system
            
            // Refresh booking data immediately
            await refreshBooking();
            
            // Close modal
            setIsSelectServiceModalOpen(false);
            
            // Check if service has a workflow
            try {
              const workflow = await getWorkflowByServiceId(service.id);
              if (workflow) {
                console.log(`Service "${service.name}" has a workflow - loading workflow steps`);
                const steps = await getWorkflowSteps(workflow.id);
                setCurrentWorkflow(workflow);
                setCurrentWorkflowSteps(steps);
                setCurrentServiceIdForWorkflow(service.id);
                setIsWorkflowAssignmentModalOpen(true);
              }
            } catch (error) {
              console.error('Error checking for service workflow:', error);
            }
          } catch (err) {
            console.error('Error adding service:', err);
            setError(err instanceof Error ? err.message : 'Failed to add service');
          }
        }}
        bookingId={localBooking?.id}
        vehicleId={currentVehicleId}
      />

      <EditTitleModal
        isOpen={isEditTitleModalOpen}
        onClose={() => setIsEditTitleModalOpen(false)}
        bookingId={localBooking.id}
        currentTitle={localBooking.title}
        onUpdate={refreshBooking}
      />

      <BookingColorPickerModal
        isOpen={isBookingColorPickerModalOpen}
        onClose={() => setIsBookingColorPickerModalOpen(false)}
        onColorSelect={handleColorUpdate}
        currentColor={localBooking.color || '#3b82f6'}
        currentSecondaryColor={localBooking.secondary_color || undefined}
        businessId={localBooking.business_id}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={async () => {
          try {
            setDeleteLoading(true);
            setError(null);
            await deleteBooking(localBooking.id);
            onUpdate();
            onClose();
          } catch (err) {
            console.error('Error deleting booking:', err);
            setError(err instanceof Error ? err.message : 'Failed to delete booking');
          } finally {
            setDeleteLoading(false);
          }
        }}
        title="Delete Booking"
        message={`Are you sure you want to delete booking #${localBooking.booking_number}? This action cannot be undone.`}
        loading={deleteLoading}
      />

      {conflictWarning && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" onClick={() => setConflictWarning(null)}>
          <div className="fixed inset-0 bg-black/50" />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Scheduling Conflict</h3>
                <p className="text-sm text-gray-500">
                  This {conflictWarning.type === 'resource' ? 'resource' : 'asset'} already has bookings during this time
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 max-h-48 overflow-y-auto">
              {conflictWarning.conflicts.map((c, i) => (
                <div key={i} className={`flex items-center justify-between py-2 ${i > 0 ? 'border-t border-amber-200' : ''}`}>
                  <div>
                    <span className="text-sm font-medium text-gray-900">#{c.booking_number}</span>
                    <span className="text-sm text-gray-500 ml-2">{c.customer}</span>
                  </div>
                  <span className="text-sm font-medium text-amber-700">
                    {c.start_time && c.end_time
                      ? `${c.start_time.substring(0, 5)} - ${c.end_time.substring(0, 5)}`
                      : 'All day'}
                  </span>
                </div>
              ))}
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Do you still want to assign this {conflictWarning.type === 'resource' ? 'resource' : 'asset'}?
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setConflictWarning(null)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (conflictWarning.type === 'resource') {
                    await confirmAddResource(conflictWarning.item);
                  } else {
                    await confirmAddAsset(conflictWarning.item);
                  }
                }}
                disabled={loading}
                className="flex-1 px-4 py-2.5 rounded-xl bg-amber-600 text-sm font-medium text-white hover:bg-amber-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Assigning...' : 'Assign Anyway'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <WorkflowCompletionModal
        isOpen={isWorkflowCompletionModalOpen}
        onClose={() => {
          setIsWorkflowCompletionModalOpen(false);
          setPendingStatusChange(null);
          setStatusLoading(false);
        }}
        incompleteSteps={incompleteWorkflowSteps}
        onCompleteAll={handleCompleteAllWorkflowSteps}
        onProceed={handleProceedWithCompletion}
      />

      <FlatRateCompletionModal
        isOpen={isFlatRateCompletionModalOpen}
        onClose={() => {
          setIsFlatRateCompletionModalOpen(false);
          setPendingStatusChange(null);
          setStatusLoading(false);
        }}
        incompleteGroups={incompleteFlatRateGroups}
        onCompleteAll={handleCompleteAllFlatRateGroups}
        onProceed={handleProceedWithFlatRateCompletion}
      />

      {isBookingDocsGateOpen && selectedBusiness?.id && localBooking?.id && (
        <BookingDocumentsGateModal
          businessId={selectedBusiness.id}
          bookingId={localBooking.id}
          isAdmin={canViewAdminDashboard}
          onClose={() => {
            setIsBookingDocsGateOpen(false);
            setPendingStatusChange(null);
            setStatusLoading(false);
          }}
          onProceed={() => {
            setIsBookingDocsGateOpen(false);
            setPendingStatusChange(null);
            handleStatusChange('completed', { skipDocGate: true });
          }}
        />
      )}

      <ApplyDiscountModal
        isOpen={isApplyDiscountModalOpen}
        onClose={() => setIsApplyDiscountModalOpen(false)}
        bookingId={localBooking.id}
        bookingTotal={localBooking.total_amount}
        onApply={refreshBooking}
        existingDiscounts={localBooking.discounts || []}
      />

      <SelectMaterialModal
        isOpen={isSelectMaterialModalOpen}
        onClose={() => setIsSelectMaterialModalOpen(false)}
        onAdd={async (item, quantity, source) => {
          try {
            if (source === 'inventory') {
              // Handle inventory item as material
              const stockItem = item as StockItem;
              
              // 1. Create stock transaction to withdraw from inventory
              await createStockTransaction({
                item_id: stockItem.id,
                type: 'sale',
                quantity: quantity,
                price: stockItem.acquisition_cost, // Use acquisition_cost, not retail price
                notes: `Added as material to booking: ${localBooking.id}`,
                booking_id: localBooking.id
              });

              // 2. Insert booking material with item_id (not material_id)
              // Log the stockItem to check for unexpected properties
              console.log('Full stockItem object:', JSON.stringify(stockItem, null, 2));
              console.log('StockItem keys:', Object.keys(stockItem));
              
              // Build insert object with material_id explicitly set to NULL
              // IMPORTANT: Explicitly set material_id to null for inventory items
              // Create a clean payload object to avoid any accidental property inclusion
              const insertPayload: Record<string, any> = {};
              insertPayload.booking_id = localBooking.id;
              insertPayload.material_id = null; // Explicitly set to null for inventory items
              insertPayload.item_id = stockItem.id;
              insertPayload.material_name = stockItem.name;
              insertPayload.quantity = quantity;
              insertPayload.unit = stockItem.unit;
              insertPayload.cost = stockItem.acquisition_cost;
              insertPayload.total_cost = stockItem.acquisition_cost * quantity;

              // Only include description if it exists
              if (stockItem.description) {
                insertPayload.material_description = stockItem.description;
              }

              // Double-check that material_id is null (not undefined, not a string, etc.)
              if (insertPayload.material_id !== null) {
                console.error('ERROR: material_id is not null!', insertPayload.material_id, typeof insertPayload.material_id);
                insertPayload.material_id = null; // Force it to null
              }

              console.log('Insert payload (material_id set to null):', JSON.stringify(insertPayload, null, 2));
              console.log('Payload keys:', Object.keys(insertPayload));
              console.log('material_id value:', insertPayload.material_id, 'type:', typeof insertPayload.material_id);

              // Prepare RPC parameters
              const rpcParams = {
                p_booking_id: localBooking.id,
                p_project_id: localBooking.project_id || null, // Include project_id if booking is linked to a project
                p_material_id: null, // Explicitly null for inventory items
                p_item_id: stockItem.id,
                p_material_name: stockItem.name,
                p_material_description: stockItem.description || null,
                p_quantity: quantity,
                p_unit: stockItem.unit,
                p_cost: stockItem.acquisition_cost,
                p_total_cost: stockItem.acquisition_cost * quantity
              };

              // Log RPC parameters with detailed type information
              console.log('=== RPC CALL DETAILS ===');
              console.log('RPC function: insert_booking_material');
              console.log('RPC parameters:', JSON.stringify(rpcParams, null, 2));
              console.log('Parameter types:', {
                p_booking_id: typeof rpcParams.p_booking_id,
                p_project_id: typeof rpcParams.p_project_id,
                p_material_id: typeof rpcParams.p_material_id,
                p_item_id: typeof rpcParams.p_item_id,
                p_material_name: typeof rpcParams.p_material_name,
                p_material_description: typeof rpcParams.p_material_description,
                p_quantity: typeof rpcParams.p_quantity,
                p_unit: typeof rpcParams.p_unit,
                p_cost: typeof rpcParams.p_cost,
                p_total_cost: typeof rpcParams.p_total_cost
              });
              console.log('p_material_id === null?', rpcParams.p_material_id === null);
              console.log('p_material_id === undefined?', rpcParams.p_material_id === undefined);
              console.log('p_item_id value:', rpcParams.p_item_id);
              console.log('========================');

              // Use RPC function instead of direct insert to avoid PostgREST NULL handling issues
              const { data: insertedId, error: insertError } = await supabase.rpc('insert_booking_material', rpcParams);

              // Log response details
              console.log('=== RPC RESPONSE ===');
              console.log('Response data:', insertedId);
              console.log('Response error:', insertError);
              if (insertError) {
                console.error('Error code:', insertError.code);
                console.error('Error message:', insertError.message);
                console.error('Error details:', insertError.details);
                console.error('Error hint:', insertError.hint);
                console.error('Full error object:', JSON.stringify(insertError, null, 2));
                console.error('Error object keys:', Object.keys(insertError));
              }
              console.log('===================');

              if (insertError) {
                console.error('=== ERROR SUMMARY ===');
                console.error('Failed to insert booking material via RPC');
                console.error('Error:', insertError);
                console.error('RPC parameters that were sent:', rpcParams);
                console.error('Stock item ID:', stockItem.id);
                console.error('Stock item name:', stockItem.name);
                console.error('Booking ID:', localBooking.id);
                console.error('Full stock item:', JSON.stringify(stockItem, null, 2));
                console.error('====================');
                throw insertError;
              }

              console.log('✅ Successfully inserted booking material with ID:', insertedId);
            } else {
              // Handle regular material
              const material = item as Material;
              
              // Prepare RPC parameters for regular material
              const rpcParams = {
                p_booking_id: localBooking.id,
                p_project_id: localBooking.project_id || null, // Include project_id if booking is linked to a project
                p_material_id: material.id,
                p_item_id: null, // Explicitly null for regular materials
                p_material_name: material.name,
                p_material_description: material.description || null,
                p_quantity: quantity,
                p_unit: material.unit,
                p_cost: material.cost,
                p_total_cost: material.cost * quantity
              };

              // Log RPC parameters with detailed type information
              console.log('=== RPC CALL DETAILS (Regular Material) ===');
              console.log('RPC function: insert_booking_material');
              console.log('RPC parameters:', JSON.stringify(rpcParams, null, 2));
              console.log('Parameter types:', {
                p_booking_id: typeof rpcParams.p_booking_id,
                p_project_id: typeof rpcParams.p_project_id,
                p_material_id: typeof rpcParams.p_material_id,
                p_item_id: typeof rpcParams.p_item_id,
                p_material_name: typeof rpcParams.p_material_name,
                p_material_description: typeof rpcParams.p_material_description,
                p_quantity: typeof rpcParams.p_quantity,
                p_unit: typeof rpcParams.p_unit,
                p_cost: typeof rpcParams.p_cost,
                p_total_cost: typeof rpcParams.p_total_cost
              });
              console.log('p_item_id === null?', rpcParams.p_item_id === null);
              console.log('p_item_id === undefined?', rpcParams.p_item_id === undefined);
              console.log('p_material_id value:', rpcParams.p_material_id);
              console.log('===========================================');

              // Use RPC function for consistency
              const { data: insertedId, error: insertError } = await supabase.rpc('insert_booking_material', rpcParams);

              // Log response details
              console.log('=== RPC RESPONSE (Regular Material) ===');
              console.log('Response data:', insertedId);
              console.log('Response error:', insertError);
              if (insertError) {
                console.error('Error code:', insertError.code);
                console.error('Error message:', insertError.message);
                console.error('Error details:', insertError.details);
                console.error('Error hint:', insertError.hint);
                console.error('Full error object:', JSON.stringify(insertError, null, 2));
                console.error('Error object keys:', Object.keys(insertError));
              }
              console.log('======================================');

              if (insertError) {
                console.error('=== ERROR SUMMARY (Regular Material) ===');
                console.error('Failed to insert booking material via RPC');
                console.error('Error:', insertError);
                console.error('RPC parameters that were sent:', rpcParams);
                console.error('Material ID:', material.id);
                console.error('Material name:', material.name);
                console.error('Booking ID:', localBooking.id);
                console.error('Full material:', JSON.stringify(material, null, 2));
                console.error('========================================');
                throw insertError;
              }

              console.log('✅ Successfully inserted booking material with ID:', insertedId);
            }

            await refreshBooking();
            setIsSelectMaterialModalOpen(false);
          } catch (err) {
            console.error('Error adding material:', err);
            setError(err instanceof Error ? err.message : 'Failed to add material');
          }
        }}
        businessId={selectedBusiness?.id}
      />

      <CreateVehicleModal
        isOpen={isCreateVehicleModalOpen}
        onClose={() => setIsCreateVehicleModalOpen(false)}
        onSuccess={async (vehicle) => {
          // Update customer data to get the new vehicle
          const updatedCustomer = await getCustomer(localBooking.customer?.id || '');
          if (updatedCustomer) {
            setLocalBooking(prev => prev ? {
              ...prev,
              customer: updatedCustomer
            } : null);
          }
          
          // Set the new vehicle as current and open service selection
          setCurrentVehicle(vehicle);
          setCurrentVehicleId(vehicle.id);
          setIsCreateVehicleModalOpen(false);
          setIsSelectServiceModalOpen(true);
        }}
        customerId={localBooking.customer?.id || ''}
      />

      {/* Workflow Resource Assignment Modal */}
      {/* Workflow system temporarily disabled during refactor */}

      {/* Warranty Booking Modal - Moved to App level */}

      <CreateProposalModal
        isOpen={isCreateProposalFromBookingOpen}
        onClose={() => setIsCreateProposalFromBookingOpen(false)}
        onSuccess={async (createdProposal) => {
          setIsCreateProposalFromBookingOpen(false);
          if (localBooking?.id) {
            await loadBookingProposals(localBooking.id);
          }
          if (createdProposal?.id) {
            try {
              const fullProposal = await getProposal(createdProposal.id);
              setSelectedProposalForView(fullProposal);
              setIsProposalDetailsOpen(true);
            } catch (err) {
              console.error('Error loading created proposal:', err);
            }
          }
        }}
        initialCustomer={localBooking?.customer ? {
          id: localBooking.customer.id,
          first_name: localBooking.customer.first_name || '',
          last_name: localBooking.customer.last_name || '',
          email: localBooking.customer.email || '',
          phone: localBooking.customer.phone || '',
          business_id: selectedBusiness?.id || '',
        } as any : undefined}
        initialBookingId={localBooking?.id || null}
      />

      {selectedProposalForView && (
        <ProposalDetailsModal
          isOpen={isProposalDetailsOpen}
          onClose={() => {
            setIsProposalDetailsOpen(false);
            setSelectedProposalForView(null);
          }}
          proposal={selectedProposalForView}
          onUpdate={async (_updatedProposal: Proposal) => {
            if (localBooking?.id) {
              await loadBookingProposals(localBooking.id);
            }
          }}
        />
      )}

      {/* Schedule a field quote directly from this booking (auto-links on success) */}
      {isScheduleFqOpen && (
        <ScheduleFieldQuoteModal
          isOpen={isScheduleFqOpen}
          onClose={() => setIsScheduleFqOpen(false)}
          projectId={null}
          projectTitle={null}
          initialCustomer={scheduleFqCustomer}
          initialOpportunityId={localBooking?.pipeline_opportunities_id ?? null}
          onSuccess={async (createdFieldQuoteId?: string) => {
            setIsScheduleFqOpen(false);
            if (createdFieldQuoteId && localBooking?.id && selectedBusiness?.id) {
              try {
                await linkFieldQuotesToBooking(localBooking.id, [createdFieldQuoteId], selectedBusiness.id);
              } catch (err) {
                console.error('Error linking field quote to booking:', err);
              }
            }
            await refreshHasFieldQuote();
          }}
        />
      )}

    </div>
    </ModalStackProvider>
  );
  
  // Portal to body so fixed layout is not clipped/trapped by control-centre panel stacking.
  if (typeof document !== 'undefined') {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
}